import type { ArtifactFormat, GeneratedIssueContent, IssueTemplate } from '@tsuzuri/contracts';

import { assertSupportedFormat, renderProfiles, templateManifests } from './manifest';
import { magazineEditorialStyles } from './magazine-editorial-styles';
import { quietCoverStyles } from './quiet-cover-styles';
import { newspaperEditorialStyles } from './newspaper-editorial-styles';
import { editorialSeriesStyles } from './editorial-series-styles';
import { brandWatermarkTileSvg } from './brand-wordmark';

export interface RenderImage {
  url: string;
  alt: string;
  focalX?: number;
  focalY?: number;
  /** Local editors may identify the source slot; production renders omit this field. */
  editorSlot?: number;
}

export interface RenderIssueInput {
  template: IssueTemplate;
  templateVersion: string;
  format: ArtifactFormat;
  content: GeneratedIssueContent;
  issueLabel: string;
  publishedOn: string;
  images?: RenderImage[];
  /** Show a real paper slot for choosing the first photo in local edit previews only. */
  editorPhotoPlaceholder?: boolean;
  showBrand?: boolean;
  /** Burn a repeated Premium-preview watermark into the rendered pages, independently of brand. */
  showPremiumWatermark?: boolean;
  locale?: 'ja-JP';
}

/**
 * The watermark is a page overlay rather than UI chrome, so Browser Run bakes it into both
 * screenshots and PDFs. The inline SVG keeps the pattern self-contained and avoids another
 * network dependency during rendering.
 */
const premiumWatermarkStyles = `
body[data-premium-watermark='true'] .page::after{
  content:'';
  position:absolute;
  z-index:2147483646;
  inset:0;
  pointer-events:none;
  background-image:url("data:image/svg+xml,${encodeURIComponent(brandWatermarkTileSvg()).replaceAll("'", '%27')}");
  background-repeat:repeat;
  background-position:center;
  background-size:calc(var(--page-w) * .26) calc(var(--page-w) * .1444);
  -webkit-print-color-adjust:exact;
  print-color-adjust:exact;
}
`;

const baseStyles = `
@import url('/fonts/noto-sans-jp.css');
@import url('/fonts/noto-serif-jp.css');
@import url('/fonts/mochiy-pop-one.css');
@import url('/fonts/m-plus-rounded-1c.css');
@import url('/fonts/editorial.css');
*{box-sizing:border-box}html,body{margin:0;padding:0;background:#eee}body{font-family:'Noto Sans JP Variable',sans-serif;color:#28251f}
.page{position:relative;overflow:hidden;background:#fbfaf6;width:var(--page-w);height:var(--page-h);padding:var(--safe);isolation:isolate}
.brand{position:absolute;right:var(--safe);bottom:calc(var(--safe) / 2);z-index:9;font-size:8px;letter-spacing:.16em;color:#7f776b}.brand b{color:#b75d43}
.eyebrow{text-transform:uppercase;letter-spacing:.18em;font-size:10px;font-weight:700}.headline{margin:0;text-wrap:balance}.article h2,.article p{margin:0}.article p{white-space:pre-wrap;line-height:1.75}.photo{display:block;width:100%;height:100%;object-fit:cover;background:#ddd}.empty-photo{display:grid;place-items:center;background:linear-gradient(135deg,#e8dfd3,#d9c8b8);color:#71665b}
[data-overflow-check]{overflow:hidden}
.page{break-after:page}.page:last-of-type{break-after:auto}
.issue-body-page{display:flex;flex-direction:column;gap:calc(var(--page-w) * .012);font-family:'Noto Serif JP Variable',serif;color:#28251f}
.issue-body-page .body-folio{display:flex;justify-content:space-between;align-items:baseline;gap:calc(var(--page-w) * .02);padding-bottom:calc(var(--page-w) * .008);border-bottom:1px solid rgba(40,37,31,.3);font-size:calc(var(--page-w) * .0125);letter-spacing:.08em;color:#6f675b}
.issue-body-page .body-copy{flex:1;min-height:0;font-size:calc(var(--page-w) * .0175);line-height:1.85;overflow-wrap:anywhere}
.issue-body-page .body-copy>*{margin:0 0 calc(var(--page-w) * .015)}
.issue-body-page .body-copy>*:first-child{margin-top:0}
.issue-body-page .body-copy>*:last-child{margin-bottom:0}
.issue-body-page .body-title{font-size:calc(var(--page-w) * .028);line-height:1.4;text-wrap:balance}
.issue-body-page .body-heading{font-size:calc(var(--page-w) * .0235);line-height:1.5;margin-top:calc(var(--page-w) * .022)}
.issue-body-page .body-lead{font-weight:700}
.issue-body-page .body-text{white-space:pre-wrap}
.issue-body-page .body-caption{font-size:calc(var(--page-w) * .0135);color:#6f675b}
.issue-body-page .body-note{border-left:2px solid #b75d43;padding-left:calc(var(--page-w) * .012)}
`;

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

const displayFieldLabels = {
  'publication-title': '題字',
  'publication-tagline': 'キャッチコピー',
  'publication-subtitle': '副題',
  'issue-label': '号表記',
  'publication-date': '発行日',
  'volume-label': '巻号',
  'audience-label': '対象年齢',
  'lead-kicker': 'トップ記事の肩見出し',
  'family-message-heading': '家族メッセージの見出し',
  'family-message-signature': '家族メッセージの署名',
  'news-heading': 'ニュース欄の見出し',
  'top-news-heading': 'トップニュースの見出し',
  'note-heading': 'ひとこと欄の見出し',
  'note-signature': 'ひとこと欄の署名',
  'milestones-heading': 'できごと欄の見出し',
  'small-news-heading': '小さなニュース欄の見出し',
  'best-shot-heading': 'ベストショット欄の見出し',
  'topics-heading': 'トピックス欄の見出し',
  'feature-heading': '特集欄の見出し',
  'achievements-heading': 'できたこと欄の見出し',
  'favorites-heading': 'お気に入り欄の見出し',
  'record-heading': '記録欄の見出し',
  'record-publication-date-label': '記録欄の発行日ラベル',
  'record-issue-label': '記録欄の号名ラベル',
  'record-headline-label': '記録欄の主見出しラベル',
  'record-article-count-label': '記録欄の記事数ラベル',
  'record-photo-count-label': '記録欄の写真数ラベル',
  'record-publisher-label': '記録欄の発行元ラベル',
  'record-cover-label': '記録欄の表紙ラベル',
  'record-feature-label': '記録欄の特集ラベル',
  'record-achievements-label': '記録欄のできごとラベル',
  'record-favorites-label': '記録欄のお気に入りラベル',
  'cover-badge-kicker': '表紙バッジの肩見出し',
  'cover-badge-title': '表紙バッジの見出し',
  'left-feature-label-1': '左の特集1のラベル',
  'left-feature-label-2': '左の特集2のラベル',
  'left-feature-label-3': '左の特集3のラベル',
  'circle-feature-label': '丸い特集のラベル',
  'photo-note-heading': '写真欄の見出し',
} as const;

type DisplayFieldId = keyof typeof displayFieldLabels;

const displayFieldMaxChars: Record<DisplayFieldId, number> = {
  'publication-title': 24,
  'publication-tagline': 60,
  'publication-subtitle': 40,
  'issue-label': 24,
  'publication-date': 24,
  'volume-label': 16,
  'audience-label': 24,
  'lead-kicker': 32,
  'family-message-heading': 32,
  'family-message-signature': 20,
  'news-heading': 32,
  'top-news-heading': 32,
  'note-heading': 32,
  'note-signature': 20,
  'milestones-heading': 32,
  'small-news-heading': 32,
  'best-shot-heading': 32,
  'topics-heading': 32,
  'feature-heading': 32,
  'achievements-heading': 32,
  'favorites-heading': 32,
  'record-heading': 32,
  'record-publication-date-label': 12,
  'record-issue-label': 12,
  'record-headline-label': 12,
  'record-article-count-label': 12,
  'record-photo-count-label': 12,
  'record-publisher-label': 12,
  'record-cover-label': 12,
  'record-feature-label': 12,
  'record-achievements-label': 12,
  'record-favorites-label': 12,
  'cover-badge-kicker': 24,
  'cover-badge-title': 24,
  'left-feature-label-1': 32,
  'left-feature-label-2': 32,
  'left-feature-label-3': 32,
  'circle-feature-label': 24,
  'photo-note-heading': 32,
};

interface ResolvedDisplayField {
  value: string;
  html: string;
  attributes: string;
  authored: boolean;
}

/**
 * Resolve editable cover furniture without trusting stored markup. Defaults deliberately bypass
 * normalization and truncation so an issue without displayText renders byte-for-byte the same
 * wording as the established template. Only authored overrides are normalized and clamped.
 */
function displayField(
  content: GeneratedIssueContent,
  id: DisplayFieldId,
  fallback: string,
  options: { secondary?: boolean } = {},
): ResolvedDisplayField {
  const hasOverride = Object.prototype.hasOwnProperty.call(content.displayText ?? {}, id);
  const override = content.displayText?.[id];
  const normalized = typeof override === 'string' ? override.trim().replace(/\s+/gu, ' ') : '';
  const glyphs = Array.from(normalized);
  const maxChars = displayFieldMaxChars[id];
  const value = !hasOverride
    ? fallback
    : glyphs.length <= maxChars
      ? normalized
      : `${glyphs.slice(0, Math.max(1, maxChars - 1)).join('')}…`;
  const attributes = ` data-tsuzuri-display-field="${id}" data-tsuzuri-display-field-label="${escapeHtml(displayFieldLabels[id])}"${options.secondary ? ' data-tsuzuri-edit-target="secondary"' : ''}`;
  return { value, html: escapeHtml(value), attributes, authored: hasOverride };
}

function displayTextFit(value: string, capacity: number, minimum = 0.4): number {
  const units = Array.from(value).reduce((total, glyph) => {
    if (/\s/u.test(glyph)) return total + 0.35;
    if (/[A-Za-z0-9]/u.test(glyph)) return total + 0.62;
    if (/^[\p{P}\p{S}]$/u.test(glyph)) return total + 0.55;
    return total + 1;
  }, 0);
  return Number(Math.min(1, Math.max(minimum, capacity / Math.max(1, units))).toFixed(3));
}

/** Apply issue metadata overrides once so cover repeats and PDF body folios cannot drift. */
function normalizeIssueDisplayMetadata(input: RenderIssueInput): RenderIssueInput {
  const issueLabel = displayField(input.content, 'issue-label', input.issueLabel).value;
  const publishedOn = displayField(input.content, 'publication-date', input.publishedOn).value;
  return issueLabel === input.issueLabel && publishedOn === input.publishedOn
    ? input
    : { ...input, issueLabel, publishedOn };
}

type RenderArticle = GeneratedIssueContent['articles'][number];

/**
 * AI copy is sequential, while the manual editor can target a fixed cover slot directly.
 * Keep those authored positions sparse here; the full-text pages still use the compact list
 * of real articles, so an empty cover slot never becomes fake saved copy.
 */
function layoutArticleSlots(content: GeneratedIssueContent): Array<RenderArticle | undefined> {
  if (!content.articles.some((article) => article.layoutSlot !== undefined)) {
    return content.articles;
  }
  const slots: Array<RenderArticle | undefined> = [];
  let nextSequentialSlot = 0;
  for (const article of content.articles) {
    const requestedSlot = article.layoutSlot;
    if (requestedSlot !== undefined && slots[requestedSlot] === undefined) {
      slots[requestedSlot] = article;
      continue;
    }
    while (slots[nextSequentialSlot] !== undefined) nextSequentialSlot += 1;
    slots[nextSequentialSlot] = article;
    nextSequentialSlot += 1;
  }
  return slots;
}

/**
 * Newsprint columns are fixed rectangles, so a slot that cannot hold the whole
 * story is cut at the last sentence that fits rather than allowed to overflow
 * and fail the readiness gate.
 */
function clampCopy(value: string, maxChars: number): string {
  const text = value.trim();
  if (text.length <= maxChars) return text;
  const slice = text.slice(0, maxChars);
  const stop = Math.max(slice.lastIndexOf('。'), slice.lastIndexOf('」'));
  if (stop >= Math.floor(maxChars * 0.45)) return slice.slice(0, stop + 1);
  return `${slice.slice(0, Math.max(1, maxChars - 1))}…`;
}

/**
 * Newsprint slots are fixed, but family copy varies wildly in length, so each
 * block gets a body-size multiplier that makes its text fill the 段 it was
 * given. `capacity` is the slot's character count at multiplier 1, measured in
 * the render browser; it is format-independent because the type scale tracks the
 * page width. Bounds keep the result within newspaper body-type proportions.
 */
function fitMultiplier(chars: number, capacity: number, target = 0.86, max = 1.34): number {
  if (chars <= 0) return 1;
  const raw = Math.sqrt((capacity * target) / chars);
  return Number(Math.min(max, Math.max(0.84, raw)).toFixed(3));
}

/**
 * Breaks a headline the way a 整理記者 or 表紙デザイナー does: at a punctuation mark if
 * there is one, so stacked display type reads as phrases rather than being chopped at an
 * arbitrary character. `minFill` is how short a line the break is allowed to leave, as a
 * share of the measure — a 見出し keeps its lines close in length, but a cover title takes
 * a lopsided pair over a word split down the middle.
 */
function wrapLines(value: string, perLine: number, maxLines: number, minFill = 0.5): string[] {
  const text = value.trim();
  const marks = /[、。　 ]/u;
  const lines: string[] = [];
  let cursor = 0;
  while (cursor < text.length && lines.length < maxLines) {
    if (text.length - cursor <= perLine) {
      lines.push(text.slice(cursor));
      break;
    }
    const window = text.slice(cursor, cursor + perLine + 1);
    let cut = -1;
    for (let index = window.length - 1; index >= perLine * minFill; index -= 1) {
      if (marks.test(window[index] ?? '')) {
        cut = index;
        break;
      }
    }
    const end = cut > 0 ? cut + 1 : perLine;
    lines.push(text.slice(cursor, cursor + end));
    cursor += end;
  }
  return lines.map((line) => line.replace(/^[、。　 ]+/u, ''));
}

/** 縦中横: short digit runs stay upright and read horizontally inside vertical Japanese copy. */
function verticalDigits(escaped: string): string {
  return escaped.replaceAll(/\d+/gu, (run) =>
    run.length <= 4 ? `<span class="np-tcy">${run}</span>` : run,
  );
}

function imageMarkup(image: RenderImage | undefined, className = 'photo'): string {
  if (!image) return '<div class="empty-photo" data-tsuzuri-photo-index="0">家族の一枚</div>';
  const focalX = Math.min(100, Math.max(0, image.focalX ?? 50));
  const focalY = Math.min(100, Math.max(0, image.focalY ?? 50));
  const editorSlot =
    image.editorSlot === undefined ? '' : ` data-tsuzuri-photo-index="${image.editorSlot}"`;
  return `<img class="${className}"${editorSlot} src="${escapeHtml(image.url)}" alt="${escapeHtml(image.alt)}" style="object-position:${focalX}% ${focalY}%" />`;
}

function secondaryImagesMarkup(images: RenderImage[] | undefined, className: string): string {
  const secondary = images?.slice(1, 4) ?? [];
  if (secondary.length === 0) return '';
  return `<div class="${className}">${secondary
    .map((image) => imageMarkup(image, 'photo secondary-photo'))
    .join('')}</div>`;
}

/** A celebratory family newspaper inspired by Japanese keepsake front pages. */
function classicNewspaper(input: RenderIssueInput): string {
  const articles = layoutArticleSlots(input.content);
  const leadArticle = articles[0];
  const newsArticle = articles[1];
  const milestoneArticle = articles[2];
  const smallNewsArticle = articles[3];
  const compact = input.format === 'sns_4x5_jpeg';
  const esc = (value: string): string => escapeHtml(value);
  const vertical = (value: string): string => verticalDigits(esc(value));
  const editorsNote = clampCopy(
    input.content.editorsNote ?? input.content.lead ?? input.issueLabel,
    compact ? 105 : 165,
  );
  const bannerTitle = clampCopy(input.content.title, compact ? 18 : 22);
  const bannerFit = Number(
    Math.min(1.08, Math.max(0.5, 11 / Math.max(1, bannerTitle.length))).toFixed(3),
  );
  const deckTitle = clampCopy(leadArticle?.headline ?? input.content.title, 18);
  const deckFit = Number(
    Math.min(1, Math.max(0.4, 6.2 / Math.max(1, deckTitle.length))).toFixed(3),
  );
  const leadBody = clampCopy(leadArticle?.body ?? '', compact ? 250 : 380);
  const storyFit = fitMultiplier(leadBody.length, compact ? 250 : 350, 0.88, 1.04);
  const leadCaption =
    leadArticle?.caption ?? input.images?.[0]?.alt ?? `${input.issueLabel}の家族写真`;
  const milestoneBodyLimit = input.images?.[2] ? (compact ? 58 : 64) : compact ? 70 : 88;
  const smallNewsBodyLimit = input.images?.[3] ? (compact ? 54 : 60) : compact ? 66 : 82;

  const topicItems = articles.slice(4, 8).some(Boolean)
    ? articles
        .slice(4, 8)
        .flatMap((article, index) =>
          article
            ? [
                `<li data-tsuzuri-article-index="${index + 4}">${esc(clampCopy(article.headline, 20))}</li>`,
              ]
            : [],
        )
        .join('')
    : `<li>${esc(input.issueLabel)}</li>`;

  const optionalCut = (index: number, className: string): string => {
    const image = input.images?.[index];
    return image ? `<div class="${className}">${imageMarkup(image, 'photo')}</div>` : '';
  };

  const dateMatch = /^(\d{4})年\s*(\d{1,2})月\s*(\d{1,2})日/u.exec(input.publishedOn);
  const weekdayNames = ['日', '月', '火', '水', '木', '金', '土'];
  const publishedYear = dateMatch ? `${dateMatch[1]}年` : input.issueLabel;
  const publishedDate = dateMatch
    ? `${Number(dateMatch[2])}月${Number(dateMatch[3])}日`
    : input.publishedOn;
  const publishedWeekday = dateMatch
    ? `${weekdayNames[new Date(Date.UTC(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]))).getUTCDay()]}曜日`
    : '発行日';
  const publicationTitle = displayField(input.content, 'publication-title', 'こども新聞');
  const publicationTitleFit = displayTextFit(publicationTitle.value, 5.4, 0.42);
  const publicationTitleSecondary = displayField(input.content, 'publication-title', 'こども新聞', {
    secondary: true,
  });
  const publicationSubtitle = displayField(input.content, 'publication-subtitle', 'わが家の編集室');
  const publicationSubtitleMarkup =
    publicationSubtitle.value === 'わが家の編集室'
      ? 'わが家の<br />編集室'
      : publicationSubtitle.html;
  const issueLabel = displayField(input.content, 'issue-label', input.issueLabel);
  const issueLabelSecondary = displayField(input.content, 'issue-label', input.issueLabel, {
    secondary: true,
  });
  const publicationDate = displayField(input.content, 'publication-date', input.publishedOn);
  const mastheadIssueLabel = displayField(input.content, 'issue-label', publishedYear, {
    secondary: true,
  });
  const mastheadPublicationDate = displayField(input.content, 'publication-date', publishedDate, {
    secondary: true,
  });
  const leadKicker = displayField(input.content, 'lead-kicker', '今月のようす');
  const familyMessageHeading = displayField(
    input.content,
    'family-message-heading',
    '家族からのメッセージ',
  );
  const familyMessageSignature = displayField(
    input.content,
    'family-message-signature',
    '家族より',
  );
  const newsHeading = displayField(input.content, 'news-heading', '今月のニュース');
  const topNewsHeading = displayField(input.content, 'top-news-heading', '今月のトップニュース');
  const noteHeading = displayField(input.content, 'note-heading', '今月のひとこと');
  const noteSignature = displayField(input.content, 'note-signature', '編集部より');
  const milestonesHeading = displayField(input.content, 'milestones-heading', '今月のできごと');
  const smallNewsHeading = displayField(input.content, 'small-news-heading', '小さなニュース');
  const bestShotHeading = displayField(input.content, 'best-shot-heading', '今月のベストショット');
  const topicsHeading = displayField(input.content, 'topics-heading', '今月のトピックス');
  const coverBadgeTitle = displayField(input.content, 'cover-badge-title', '成長号外');
  const coverBadgeGlyphs = Array.from(coverBadgeTitle.value);
  const coverBadgeCut = Math.ceil(coverBadgeGlyphs.length / 2);
  const coverBadgeMarkup = [
    coverBadgeGlyphs.slice(0, coverBadgeCut),
    coverBadgeGlyphs.slice(coverBadgeCut),
  ]
    .filter((column) => column.length > 0)
    .map((column) => `<span>${column.map((glyph) => esc(glyph)).join('<br />')}</span>`)
    .join('');

  return `<main class="page newspaper" data-newspaper-layout="japanese" data-newspaper-layout-style="commemorative-frontpage" data-newspaper-composition="keepsake" style="--np-name-fit:${publicationTitleFit}">
    <div class="np-top-rail" data-newspaper-region="top-rail" data-newspaper-treatment="folio-rule" aria-label="紙面情報">
      <span data-newspaper-folio="date"${publicationDate.attributes}>（1） ${publicationDate.html}</span>
      <b data-newspaper-folio="name"${publicationTitle.attributes}>${publicationTitle.html}</b>
      <span data-newspaper-folio="issue"${issueLabel.attributes}>${issueLabel.html}${issueLabel.authored ? '' : '（記念号）'}</span>
    </div>

    <div class="newspaper-spread" data-newspaper-region="spread" data-newspaper-split="84-16">
      <section class="newspaper-main" data-newspaper-region="main-column" data-newspaper-rows="4" aria-label="今月の家族新聞">
        <header class="np-headline-banner" data-newspaper-region="headline-banner" data-newspaper-treatment="black-banner" data-overflow-check style="--np-title-fit:${bannerFit}">
          <h1 data-newspaper-region="main-headline" data-tsuzuri-edit="title">${esc(bannerTitle)}</h1>
        </header>

        <figure class="np-lead-photo" data-newspaper-region="lead-photo" data-newspaper-photo-footprint="dominant">
          <div class="np-photo-frame">${imageMarkup(input.images?.[0])}</div>
          <figcaption${leadArticle ? ' data-tsuzuri-article-index="0"' : ''} data-tsuzuri-edit-target="secondary" data-newspaper-region="photo-caption">${esc(clampCopy(leadCaption, 48))}</figcaption>
        </figure>

        <section class="np-title-deck"${leadArticle ? ' data-tsuzuri-article-index="0"' : ''} data-newspaper-region="title-deck" data-tsuzuri-edit="stories" data-overflow-check style="--np-deck-fit:${deckFit}">
          <h2>${esc(deckTitle)}</h2>
          <p${leadKicker.attributes}>${leadKicker.html}</p>
        </section>

        <section class="np-editorial-band" data-newspaper-region="editorial-band" data-newspaper-layout="asymmetric" data-tsuzuri-edit="stories" aria-label="今月の特集">
          <article class="np-message-card" data-tsuzuri-content-field="editorsNote" data-newspaper-card="message" data-newspaper-frame="double-inset" data-overflow-check>
            <h3${familyMessageHeading.attributes}>${familyMessageHeading.html}</h3>
            <p>${esc(editorsNote)}</p>
            <small${familyMessageSignature.attributes}>${familyMessageSignature.html}</small>
          </article>

          <article class="np-news-card"${newsArticle ? ' data-tsuzuri-article-index="1"' : ''} data-newspaper-card="news" data-newspaper-frame="double-inset" data-newspaper-article="secondary" data-overflow-check>
            <h3${newsHeading.attributes}>${newsHeading.html}</h3>
            ${newsArticle ? `<h4>${esc(clampCopy(newsArticle.headline, 24))}</h4><p>${esc(clampCopy(newsArticle.body, compact ? 88 : 145))}</p>` : `<p>${esc(clampCopy(input.content.lead ?? input.issueLabel, compact ? 88 : 145))}</p>`}
          </article>

          <article class="np-feature-story"${leadArticle ? ' data-tsuzuri-article-index="0"' : ''} data-newspaper-region="lead-story" data-newspaper-article="lead" data-overflow-check>
            <div class="np-story-copy" data-newspaper-region="story-copy" data-newspaper-columns="vertical" data-text-flow="vertical" data-overflow-check style="--np-story-fit:${storyFit}">
              <h3${topNewsHeading.attributes}>${topNewsHeading.html}</h3>
              <p>${vertical(leadBody)}</p>
            </div>
          </article>
        </section>

      </section>

      <aside class="np-right-rail" data-newspaper-region="right-rail">
        <header class="newspaper-masthead" data-newspaper-region="masthead">
          <h1 data-newspaper-region="nameplate" data-text-flow="vertical"${publicationTitleSecondary.attributes}><span>${publicationTitleSecondary.html}</span></h1>
        </header>

        <p class="np-publisher"${publicationSubtitle.attributes}>${publicationSubtitleMarkup}</p>

        <div class="np-masthead-meta" data-newspaper-region="issue-meta">
          <span${mastheadIssueLabel.attributes}>${mastheadIssueLabel.html}</span>
          <time${mastheadPublicationDate.attributes}>${mastheadPublicationDate.html}</time>
          <b>${esc(publishedWeekday)}</b>
        </div>

        <section class="np-extra-edition" data-newspaper-region="extra-edition" data-newspaper-treatment="red-extra-edition" aria-label="成長号外">
          <strong${coverBadgeTitle.attributes}>${coverBadgeMarkup}</strong>
          <p${issueLabelSecondary.attributes}>${issueLabelSecondary.html}</p>
        </section>

        <aside class="np-rail-note" data-tsuzuri-content-field="lead" data-newspaper-region="editorial-column" data-newspaper-card="editorial" data-newspaper-frame="corner-rule" data-tsuzuri-edit="stories" data-overflow-check>
          <h2${noteHeading.attributes}>${noteHeading.html}</h2>
          <p data-newspaper-region="editorial-copy">${esc(clampCopy(input.content.lead ?? input.issueLabel, compact ? 48 : 65))}</p>
          <small${noteSignature.attributes}>${noteSignature.html}</small>
        </aside>
      </aside>
    </div>

    <section class="np-lower-card-grid" data-newspaper-region="lower-card-grid" data-newspaper-layout="full-width-asymmetric" data-newspaper-span="full-page" data-tsuzuri-edit="stories" aria-label="今月のニュース欄">
      <article class="np-milestones-card"${milestoneArticle ? ' data-tsuzuri-article-index="2"' : ''} data-newspaper-card="milestones" data-newspaper-frame="single-rule" data-newspaper-region="milestones-card" data-newspaper-article="secondary" data-overflow-check>
        <h3${milestonesHeading.attributes}>${milestonesHeading.html}</h3>
        ${milestoneArticle ? `<h4>☆ ${esc(clampCopy(milestoneArticle.headline, 22))}</h4><p>${esc(clampCopy(milestoneArticle.body, milestoneBodyLimit))}</p>` : `<p>${esc(clampCopy(input.content.lead ?? input.issueLabel, milestoneBodyLimit))}</p>`}
        ${optionalCut(2, 'np-card-photo-cut')}
      </article>

      <article class="np-small-news-card"${smallNewsArticle ? ' data-tsuzuri-article-index="3"' : ''} data-newspaper-card="favorites" data-newspaper-frame="single-rule" data-newspaper-region="favorites-card" data-newspaper-article="secondary" data-overflow-check>
        <h3${smallNewsHeading.attributes}>${smallNewsHeading.html}</h3>
        ${smallNewsArticle ? `<h4>${esc(clampCopy(smallNewsArticle.headline, 22))}</h4><p>${esc(clampCopy(smallNewsArticle.body, smallNewsBodyLimit))}</p>` : `<p>${esc(clampCopy(newsArticle?.headline ?? input.issueLabel, smallNewsBodyLimit))}</p>`}
        ${optionalCut(3, 'np-card-photo-cut')}
      </article>

      <figure class="np-best-shot-card" data-newspaper-card="gallery" data-newspaper-frame="single-rule" data-newspaper-region="best-shots" data-overflow-check>
        <h3${bestShotHeading.attributes}>${bestShotHeading.html}</h3>
        <div class="np-best-shot">${input.images?.[1] ? imageMarkup(input.images[1], 'photo') : `<p>${esc(clampCopy(leadCaption, 44))}</p>`}</div>
        <figcaption${input.images?.[1] ? ' data-tsuzuri-article-index="1"' : leadArticle ? ' data-tsuzuri-article-index="0"' : ''} data-tsuzuri-edit-target="secondary">${esc(clampCopy(input.images?.[1]?.alt ?? leadCaption, 32))}</figcaption>
      </figure>

      <article class="np-topics-card" data-tsuzuri-article-group="classic-topics" data-tsuzuri-article-group-label="${topicsHeading.html}" data-newspaper-card="topics" data-newspaper-frame="single-rule" data-newspaper-region="topics-card" data-newspaper-article="lower" data-overflow-check>
        <h3${topicsHeading.attributes}>${topicsHeading.html}</h3>
        <ul>${topicItems}</ul>
      </article>
    </section>

    ${input.showBrand === false ? '' : '<div class="brand">きみの今を、未来のきみへ。</div>'}
  </main>`;
}

/** A fully horizontal family broadsheet with a hero story and compact editorial decks. */
function horizontalNewspaper(input: RenderIssueInput): string {
  const articles = layoutArticleSlots(input.content);
  const compact = input.format === 'sns_4x5_jpeg';
  const esc = (value: string): string => escapeHtml(value);
  const normalized = (value: string): string => value.replace(/\s+/gu, ' ').trim();
  const copy = (value: string, maxChars: number): string => {
    const text = normalized(value);
    if (text.length <= maxChars) return text;
    const slice = text.slice(0, maxChars);
    const stop = Math.max(
      slice.lastIndexOf('。'),
      slice.lastIndexOf('！'),
      slice.lastIndexOf('？'),
    );
    return stop >= Math.floor(maxChars * 0.45) ? slice.slice(0, stop + 1) : slice.trimEnd();
  };
  const splitColumns = (value: string, count: number, maxChars: number): string[] => {
    const source = copy(value, maxChars);
    const columns: string[] = [];
    let rest = source;
    for (let index = 0; index < count; index += 1) {
      if (index === count - 1) {
        columns.push(rest);
        break;
      }
      const remainingColumns = count - index;
      const target = Math.max(1, Math.ceil(rest.length / remainingColumns));
      const floor = Math.max(1, Math.floor(target * 0.68));
      const ceiling = Math.min(rest.length, Math.ceil(target * 1.24));
      let cut = -1;
      for (let cursor = ceiling; cursor >= floor; cursor -= 1) {
        if (/[。！？]/u.test(rest[cursor - 1] ?? '')) {
          cut = cursor;
          break;
        }
      }
      if (cut < 0) cut = Math.min(target, rest.length);
      columns.push(rest.slice(0, cut).trim());
      rest = rest.slice(cut).trim();
    }
    while (columns.length < count) columns.push('');
    return columns;
  };
  const listItems = (
    values: Array<{ value: string; articleIndex: number }>,
    limit: number,
    maxChars: number,
  ): string =>
    values
      .map(({ value, articleIndex }) => ({ value: normalized(value), articleIndex }))
      .filter(({ value }) => Boolean(value))
      .slice(0, limit)
      .map(
        ({ value, articleIndex }) =>
          `<li data-tsuzuri-article-index="${articleIndex}">${esc(copy(value, maxChars))}</li>`,
      )
      .join('');

  const authoredLeadArticle = articles[0];
  const leadArticle = authoredLeadArticle ?? {
    headline: input.content.title,
    body: input.content.lead ?? input.content.title,
    caption: null,
    sourceEventIds: [],
  };
  const articleEntries = (start: number, end: number) =>
    articles
      .slice(start, end)
      .flatMap((article, offset) => (article ? [{ article, articleIndex: start + offset }] : []));
  const teaserArticles = articleEntries(1, 4);
  const sideArticles = articleEntries(4, 7);
  const laterArticle = articles[7];
  const finalArticle = articles[8];
  const leadBody = copy(leadArticle.body, compact ? 240 : 280);
  const leadColumns = splitColumns(leadBody, 3, compact ? 240 : 280);
  const headline = copy(input.content.title, compact ? 20 : 22);
  const headlineFit = Number(
    Math.min(1.05, Math.max(0.65, 14 / Math.max(1, headline.length))).toFixed(3),
  );
  const leadImage = input.images?.[0];
  const showLeadPhoto = Boolean(leadImage || input.editorPhotoPlaceholder);
  const teaserImage = teaserArticles.length ? input.images?.[1] : undefined;
  const sideImage = sideArticles.length ? input.images?.[2] : undefined;
  const leadCaption = leadImage
    ? copy(leadArticle.caption ?? leadImage.alt ?? '', compact ? 45 : 55)
    : '';
  const familyMessage = input.content.editorsNote
    ? copy(input.content.editorsNote, compact ? 72 : 85)
    : '';
  const milestoneItems = listItems(
    articleEntries(1, 6).map(({ article, articleIndex }) => ({
      value: article.headline,
      articleIndex,
    })),
    5,
    compact ? 12 : 14,
  );
  const favoriteItems = listItems(
    articleEntries(4, 9).map(({ article, articleIndex }) => ({
      value: article.headline,
      articleIndex,
    })),
    5,
    compact ? 12 : 14,
  );
  const bestCaption = copy(
    input.images?.[3]?.alt ?? leadArticle.caption ?? leadArticle.headline,
    compact ? 34 : 48,
  );
  const publicationTagline = input.content.lead
    ? copy(input.content.lead, 28)
    : '家族の記録を、ひとつずつ。';
  const familySprig = `<svg class="hn-sprig-cut" data-newspaper-decoration="botanical-sprig" viewBox="0 0 72 92" aria-hidden="true"><path d="M9 86C25 72 31 55 42 36C50 22 58 12 66 6"/><ellipse cx="24" cy="68" rx="6" ry="12" transform="rotate(-42 24 68)"/><ellipse cx="34" cy="53" rx="6" ry="12" transform="rotate(42 34 53)"/><ellipse cx="44" cy="37" rx="5" ry="11" transform="rotate(-39 44 37)"/><ellipse cx="54" cy="23" rx="5" ry="10" transform="rotate(43 54 23)"/></svg>`;
  const pencilCut = `<svg class="hn-editorial-cut hn-pencil-cut" data-newspaper-decoration="pencil" viewBox="0 0 48 68" aria-hidden="true"><path d="M10 56L31 13L39 17L18 60L8 64Z"/><path d="M31 13L35 5L43 9L39 17M10 56L18 60"/></svg>`;
  const leafCut = `<svg class="hn-editorial-cut hn-leaf-cut" data-newspaper-decoration="leaf-sprig" viewBox="0 0 72 88" aria-hidden="true"><path d="M12 82C29 67 37 47 56 17"/><ellipse cx="28" cy="64" rx="7" ry="13" transform="rotate(-47 28 64)"/><ellipse cx="39" cy="49" rx="7" ry="13" transform="rotate(42 39 49)"/><ellipse cx="49" cy="32" rx="6" ry="12" transform="rotate(-42 49 32)"/></svg>`;
  const sunCut = `<svg class="hn-editorial-cut hn-sun-cut" data-newspaper-decoration="sun" viewBox="0 0 80 80" aria-hidden="true"><circle cx="40" cy="40" r="13"/><path d="M40 4V17M40 63V76M4 40H17M63 40H76M14 14L23 23M57 57L66 66M66 14L57 23M23 57L14 66"/></svg>`;

  const dateMatch = /^(\d{4})年\s*(\d{1,2})月\s*(\d{1,2})日/u.exec(input.publishedOn);
  const labelMatch = /(\d{4})年\s*(\d{1,2})月/u.exec(input.issueLabel);
  const issueYear = labelMatch?.[1] ?? dateMatch?.[1] ?? '';
  const issueMonth = labelMatch?.[2] ?? dateMatch?.[2] ?? '';
  const issueDisplay = issueMonth ? `${Number(issueMonth)}月号` : copy(input.issueLabel, 12);
  const publicationTitle = displayField(input.content, 'publication-title', 'こども新聞');
  const publicationTitleFit = displayTextFit(publicationTitle.value, 6.2, 0.42);
  const publicationTitleSecondary = displayField(input.content, 'publication-title', 'こども新聞', {
    secondary: true,
  });
  const publicationSubtitle = displayField(input.content, 'publication-subtitle', 'わが家の編集室');
  const issueLabel = displayField(input.content, 'issue-label', input.issueLabel);
  const issueDisplayField = displayField(input.content, 'issue-label', issueDisplay, {
    secondary: true,
  });
  const issueLabelRecord = displayField(input.content, 'issue-label', input.issueLabel, {
    secondary: true,
  });
  const publicationDate = displayField(input.content, 'publication-date', input.publishedOn);
  const publicationDateSecondary = displayField(
    input.content,
    'publication-date',
    input.publishedOn,
    { secondary: true },
  );
  const featureHeading = displayField(input.content, 'feature-heading', '今月の特集');
  const achievementsHeading = displayField(
    input.content,
    'achievements-heading',
    '今月のできたこと',
  );
  const favoritesHeading = displayField(input.content, 'favorites-heading', '今月のお気に入り');
  const familyMessageHeading = displayField(
    input.content,
    'family-message-heading',
    '家族からのメッセージ',
  );
  const familyMessageSignature = displayField(
    input.content,
    'family-message-signature',
    '家族より',
  );
  const recordHeading = displayField(input.content, 'record-heading', '今月の記録');
  const recordPublicationDateLabel = displayField(
    input.content,
    'record-publication-date-label',
    '発行日',
  );
  const recordIssueLabel = displayField(input.content, 'record-issue-label', '今月号');
  const recordHeadlineLabel = displayField(input.content, 'record-headline-label', '主見出し');
  const recordArticleCountLabel = displayField(
    input.content,
    'record-article-count-label',
    '掲載記事',
  );
  const recordPhotoCountLabel = displayField(input.content, 'record-photo-count-label', '掲載写真');
  const recordPublisherLabel = displayField(input.content, 'record-publisher-label', '発行');
  const noteHeading = displayField(input.content, 'note-heading', '今月のひとこと');

  const teaserMarkup = teaserArticles
    .map(({ article, articleIndex }, index) => {
      const image = index === 0 ? teaserImage : undefined;
      return `<article class="hn-teaser${image ? ' has-photo' : ''}" data-tsuzuri-article-index="${articleIndex}" data-newspaper-teaser="${articleIndex + 1}" data-overflow-check>
        ${image ? `<figure data-newspaper-image-slot="teaser">${imageMarkup(image, 'photo')}</figure>` : ''}
        <div><h2>${esc(copy(article.headline, image ? (compact ? 16 : 18) : compact ? 18 : 22))}</h2><p>${esc(copy(article.body, image ? (compact ? 26 : 30) : compact ? 42 : 54))}</p></div>
      </article>`;
    })
    .join('');

  const sideRoles = ['feature', 'daily-life', 'reading'] as const;
  const sideMarkup = sideArticles
    .map(({ article, articleIndex }, index) => {
      const image = index === 0 ? sideImage : undefined;
      return `<article class="hn-side-item${image ? ' has-photo' : ''}" data-tsuzuri-article-index="${articleIndex}" data-newspaper-module="${sideRoles[index]}" data-overflow-check>
        <h3>${index === 0 ? `<span${featureHeading.attributes}>${featureHeading.html}</span>` : ''}${esc(copy(article.headline, compact ? 16 : 18))}</h3>
        <p>${esc(copy(article.body, image ? (compact ? 36 : 42) : compact ? 50 : 64))}</p>
        ${image ? `<figure data-newspaper-image-slot="side">${imageMarkup(image, 'photo')}</figure>` : ''}
      </article>`;
    })
    .join('');

  const midModules: string[] = [];
  if (teaserArticles.length) {
    midModules.push(
      `<article data-tsuzuri-article-group="horizontal-milestones" data-tsuzuri-article-group-label="${achievementsHeading.html}" data-newspaper-module="milestones" data-newspaper-treatment="ruled-cell" data-overflow-check><h2${achievementsHeading.attributes}>${achievementsHeading.html}</h2><ul class="hn-checklist">${milestoneItems}</ul></article>`,
    );
  }
  if (sideArticles.length) {
    midModules.push(
      `<article data-tsuzuri-article-group="horizontal-favorites" data-tsuzuri-article-group-label="${favoritesHeading.html}" data-newspaper-module="favorites" data-newspaper-treatment="ruled-cell" data-overflow-check><h2${favoritesHeading.attributes}>${favoritesHeading.html}</h2><ul>${favoriteItems}</ul></article>`,
    );
  }
  if (familyMessage) {
    midModules.push(
      `<article class="hn-family-message" data-tsuzuri-content-field="editorsNote" data-newspaper-module="family-message" data-newspaper-treatment="ruled-cell" data-overflow-check><h2${familyMessageHeading.attributes}>${familyMessageHeading.html}</h2><div><p>${esc(familyMessage)}</p><small${familyMessageSignature.attributes}>${familyMessageSignature.html}</small></div>${familySprig}</article>`,
    );
  }
  let hasPhotoNoteHeading = false;
  const appendPhotoModule = (
    image: NonNullable<RenderIssueInput['images']>[number] | undefined,
    role: string,
    title: string,
    headingField: 'photo-note-heading' | 'best-shot-heading',
    slot: string,
    articleIndex: number,
  ): void => {
    if (!image) return;
    const photoNoteHeading = displayField(input.content, headingField, title, {
      secondary: headingField === 'photo-note-heading' && hasPhotoNoteHeading,
    });
    if (headingField === 'photo-note-heading') hasPhotoNoteHeading = true;
    midModules.push(
      `<figure class="hn-best-shot" data-newspaper-module="${role}" data-newspaper-treatment="photo-cell" data-overflow-check><h2${photoNoteHeading.attributes}>${photoNoteHeading.html}</h2><div data-newspaper-image-slot="${slot}">${imageMarkup(image, 'photo')}</div><figcaption data-tsuzuri-article-index="${articleIndex}" data-tsuzuri-edit-target="secondary">${esc(copy(image.alt ?? bestCaption, compact ? 34 : 48))}</figcaption></figure>`,
    );
  };
  if (!teaserImage)
    appendPhotoModule(
      input.images?.[1],
      'photo-note-1',
      '写真だより',
      'photo-note-heading',
      'mid-1',
      1,
    );
  if (!sideImage)
    appendPhotoModule(
      input.images?.[2],
      'photo-note-2',
      '今月の一枚',
      'photo-note-heading',
      'mid-2',
      4,
    );
  appendPhotoModule(
    input.images?.[3],
    'best-shot',
    '今月のベストショット',
    'best-shot-heading',
    'mid',
    0,
  );

  const lowerModules: string[] = [
    `<article class="hn-record" data-newspaper-module="monthly-record" data-newspaper-treatment="ruled-record" data-overflow-check>
      <h2${recordHeading.attributes}>${recordHeading.html}</h2>
      <dl>
        <div><dt${recordPublicationDateLabel.attributes}>${recordPublicationDateLabel.html}</dt><dd${publicationDateSecondary.attributes}>${publicationDateSecondary.html}</dd></div>
        <div><dt${recordIssueLabel.attributes}>${recordIssueLabel.html}</dt><dd${issueLabelRecord.attributes}>${issueLabelRecord.html}</dd></div>
        <div><dt${recordHeadlineLabel.attributes}>${recordHeadlineLabel.html}</dt><dd${authoredLeadArticle ? ' data-tsuzuri-article-index="0"' : ''} data-tsuzuri-edit-target="secondary">${esc(copy(leadArticle.headline, 13))}</dd></div>
        <div><dt${recordArticleCountLabel.attributes}>${recordArticleCountLabel.html}</dt><dd>${input.content.articles.length}本</dd></div>
        <div><dt${recordPhotoCountLabel.attributes}>${recordPhotoCountLabel.html}</dt><dd>${input.images?.length ?? 0}枚</dd></div>
        <div><dt${recordPublisherLabel.attributes}>${recordPublisherLabel.html}</dt><dd${publicationSubtitle.attributes}>${publicationSubtitle.html}</dd></div>
      </dl>
    </article>`,
  ];
  if (laterArticle) {
    lowerModules.push(
      `<article class="hn-watch-note" data-tsuzuri-article-index="7" data-newspaper-module="watch-note" data-newspaper-treatment="label-over-rule" data-overflow-check><h2>${esc(copy(laterArticle.headline, 14))}</h2><p>${esc(copy(laterArticle.body, compact ? 52 : 65))}</p>${pencilCut}</article>`,
    );
  }
  if (input.content.lead && input.content.articles.length >= 7) {
    lowerModules.push(
      `<article class="hn-quote" data-tsuzuri-content-field="lead" data-newspaper-module="quote" data-newspaper-treatment="quote-box" data-overflow-check><h2${noteHeading.attributes}>${noteHeading.html}</h2><blockquote>「${esc(copy(input.content.lead, compact ? 34 : 52))}」</blockquote>${leafCut}</article>`,
    );
  }
  if (finalArticle) {
    lowerModules.push(
      `<article class="hn-editorial-note" data-tsuzuri-article-index="8" data-newspaper-module="editorial-note" data-newspaper-treatment="editorial-box" data-overflow-check><h2>${esc(copy(finalArticle.headline, 14))}</h2><p>${esc(copy(finalArticle.body, compact ? 48 : 60))}</p>${sunCut}</article>`,
    );
  }

  const rootClasses = [
    !teaserArticles.length && 'hn-no-teasers',
    !midModules.length && 'hn-no-mid',
    !sideArticles.length && 'hn-no-side',
    !showLeadPhoto && 'hn-no-hero',
  ]
    .filter(Boolean)
    .join(' ');

  return `<main class="page horizontal-newspaper${rootClasses ? ` ${rootClasses}` : ''}" data-newspaper-layout="horizontal" data-newspaper-layout-style="family-broadsheet" data-newspaper-frame="hairline-page" data-text-flow="horizontal" style="--hn-name-fit:${publicationTitleFit}">
    <header class="hn-folio" data-newspaper-region="folio" data-newspaper-rule="double-bottom" data-overflow-check>
      <span data-newspaper-folio="issue"${issueLabel.attributes}>${issueLabel.html}</span>
      <span data-newspaper-folio="date"${publicationDate.attributes}>${publicationDate.html}${publicationDate.authored ? '' : '発行'}</span>
      <b data-newspaper-folio="name"${publicationTitle.attributes}>${publicationTitle.html}</b>
      <span data-newspaper-folio="tagline" data-tsuzuri-content-field="lead" data-tsuzuri-edit="stories">${esc(publicationTagline)}</span>
    </header>

    ${
      teaserArticles.length
        ? `<section class="hn-top-teasers" data-tsuzuri-article-group="horizontal-top-teasers" data-tsuzuri-article-group-label="上段の記事" data-newspaper-region="top-teasers" data-newspaper-rule="single-bottom" data-newspaper-columns="${teaserArticles.length}" data-module-count="${teaserArticles.length}" data-tsuzuri-edit="stories" aria-label="紙面案内">${teaserMarkup}</section>`
        : ''
    }

    <header class="hn-masthead-band" data-newspaper-region="masthead-band" data-newspaper-rule="single-bottom">
      <div class="hn-masthead-note"${authoredLeadArticle ? ' data-tsuzuri-article-index="0"' : ''} data-tsuzuri-edit-target="secondary" data-newspaper-region="masthead-note" data-tsuzuri-edit="stories" data-overflow-check><i aria-hidden="true"></i><p>${esc(copy(leadArticle.headline, 18))}</p></div>
      <h1 data-newspaper-region="masthead" data-text-flow="horizontal"${publicationTitleSecondary.attributes}>${publicationTitleSecondary.html}</h1>
      <div class="hn-issue-meta" data-newspaper-region="issue-meta" data-overflow-check>
        ${issueYear ? `<span>${esc(issueYear)}年</span>` : ''}
        <b${issueDisplayField.attributes}>${issueDisplayField.html}</b>
        <time${publicationDateSecondary.attributes}>${publicationDateSecondary.html}</time>
      </div>
    </header>

    <section class="hn-lead-spread${sideArticles.length ? '' : ' no-side'}" data-newspaper-region="lead-spread" data-newspaper-split="22-78">
      ${sideArticles.length ? `<aside class="hn-side-rail" data-tsuzuri-article-group="horizontal-side-rail" data-tsuzuri-article-group-label="${featureHeading.html}" data-newspaper-region="side-rail" data-newspaper-rule="single-right" data-module-count="${sideArticles.length}" data-tsuzuri-edit="stories" aria-label="${featureHeading.html}">${sideMarkup}</aside>` : ''}
      <article class="hn-lead-story${showLeadPhoto ? '' : ' no-hero'}"${authoredLeadArticle ? ' data-tsuzuri-article-index="0"' : ''} data-newspaper-region="lead-story">
        ${
          showLeadPhoto
            ? `<figure class="hn-lead-figure${leadCaption ? '' : ' no-caption'}" data-newspaper-region="lead-photo" data-newspaper-photo-footprint="dominant" data-newspaper-photo-shape="wide-landscape" data-newspaper-image-slot="lead">
          <div class="hn-lead-photo">${imageMarkup(leadImage, 'photo')}</div>
          ${leadCaption ? `<figcaption class="hn-lead-caption"${authoredLeadArticle ? ' data-tsuzuri-article-index="0"' : ''} data-tsuzuri-edit-target="secondary" data-overflow-check>${esc(leadCaption)}</figcaption>` : ''}
        </figure>`
            : ''
        }
        <h1 data-newspaper-region="main-headline" data-tsuzuri-edit="title" data-overflow-check style="--hn-headline-fit:${headlineFit}">${esc(headline)}</h1>
        <div class="hn-lead-columns" data-newspaper-region="lead-columns" data-newspaper-columns="3" data-tsuzuri-edit="stories">
          ${leadColumns.map((column, index) => `<p data-newspaper-column="${index + 1}" data-overflow-check>${esc(column)}</p>`).join('')}
        </div>
      </article>
    </section>

    ${
      midModules.length
        ? `<section class="hn-mid-grid" data-newspaper-region="mid-module-grid" data-newspaper-frame="ruled-grid" data-newspaper-columns="${midModules.length}" data-module-count="${midModules.length}" data-tsuzuri-edit="stories" aria-label="今月のまとめ">${midModules.join('')}</section>`
        : ''
    }

    <section class="hn-lower-grid" data-newspaper-region="lower-module-grid" data-newspaper-frame-align="equal-height" data-newspaper-columns="${lowerModules.length}" data-module-count="${lowerModules.length}" data-tsuzuri-edit="stories" aria-label="編集ノート">
      ${lowerModules.join('')}
    </section>
  </main>`;
}

/** A bright Japanese family-magazine cover with a framed hero and small editorial boxes. */
function magazineCover(input: RenderIssueInput): string {
  const articles = layoutArticleSlots(input.content);
  const leadArticle = articles[0];
  const achievementArticle = articles[1];
  const favoriteArticle = articles[2];
  const esc = (value: string): string => escapeHtml(value);
  const coverCopy = (value: string, maxChars: number): string =>
    clampCopy(value.replace(/\s+/gu, ' '), maxChars);
  const compact = input.format === 'sns_4x5_jpeg' || input.format === 'postcard_jpeg';
  const story = input.format === 'story_9x16_jpeg';
  const shortTitle = coverCopy(leadArticle?.headline ?? input.content.title, compact ? 13 : 16);
  const shortTitleFit = Number(
    Math.min(1, Math.max(0.46, 6.2 / Math.max(1, shortTitle.length))).toFixed(3),
  );
  const growthLines = wrapLines(coverCopy(input.content.title, story ? 30 : 26), 14, 2, 0.3);
  const longestGrowthLine = Math.max(1, ...growthLines.map((line) => line.length));
  const growthFit = Number(Math.min(1, Math.max(0.54, 11.5 / longestGrowthLine)).toFixed(3));
  const growthMarkup = growthLines.map((line) => `<span>${esc(line)}</span>`).join('');
  const deck = coverCopy(leadArticle?.body ?? '', compact ? 56 : 76);
  const note = input.content.lead ? coverCopy(input.content.lead, 36) : '';
  const familyMessage = input.content.editorsNote ? coverCopy(input.content.editorsNote, 130) : '';

  const sentences = (value: string, limit: number, maxChars: number): string[] => {
    const parts = value.match(/[^。！？]+[。！？]?/gu) ?? [];
    return parts
      .map((part) => part.trim())
      .filter(Boolean)
      .slice(0, limit)
      .map((part) => clampCopy(part, maxChars));
  };
  const articleExcerpt = (article: RenderArticle, label: string): string => {
    return `<span class="mag-section-label" aria-hidden="true">${label}</span>
      <h4 class="mag-story-title" style="--mag-story-fit:${displayTextFit(coverCopy(article.headline, 20), 15, 0.8)}">${esc(coverCopy(article.headline, 20))}</h4>
      <p class="mag-story-excerpt">${esc(coverCopy(article.body, compact ? 68 : 90))}</p>`;
  };
  const featureStories = articles.slice(3, 5).flatMap((article, index) =>
    article
      ? [
          `<article class="mag-feature-story" data-tsuzuri-article-index="${index + 3}" data-tsuzuri-edit="stories" data-overflow-check>
        <span class="mag-section-label" aria-hidden="true">FEATURE / 0${index + 1}</span>
        <h3>${esc(coverCopy(article.headline, 22))}</h3>
        <p>${esc(coverCopy(article.body, compact ? 36 : 52))}</p>
      </article>`,
        ]
      : [],
  );
  const hasExtraTopics = articles.slice(5, 8).some(Boolean);
  const topicOffset = hasExtraTopics ? 5 : 3;
  const topicSource = articles.slice(topicOffset, hasExtraTopics ? 8 : 5);
  const topicLabels = topicSource.some(Boolean)
    ? topicSource.flatMap((article, index) =>
        article
          ? [
              { label: coverCopy(article.headline, 20), articleIndex: index + topicOffset },
              ...sentences(article.body, 1, 34).map((label) => ({
                label,
                articleIndex: index + topicOffset,
              })),
            ]
          : [],
      )
    : [
        {
          label: coverCopy(leadArticle?.headline ?? input.issueLabel, 20),
          articleIndex: leadArticle ? 0 : null,
        },
      ];
  const topicItems = topicLabels
    .slice(0, 6)
    .map(
      ({ label, articleIndex }) =>
        `<li${articleIndex === null ? '' : ` data-tsuzuri-article-index="${articleIndex}"`}>${esc(label)}</li>`,
    )
    .join('');
  const issueNumber = /(\d+)\s*月/u.exec(input.issueLabel)?.[1] ?? '';
  const issueMark = issueNumber ? `${issueNumber}月号` : coverCopy(input.issueLabel, 14);
  const issueLabel = displayField(input.content, 'issue-label', issueMark);
  const issueLabelSecondary = displayField(input.content, 'issue-label', issueMark, {
    secondary: true,
  });
  const issueUnits = [...issueLabel.value].reduce((sum, character) => {
    if (/\d/u.test(character)) return sum + 0.55;
    if (/\s/u.test(character)) return sum + 0.35;
    if (/[A-Za-z]/u.test(character)) return sum + 0.62;
    return sum + 1;
  }, 0);
  const issueFit = Number(Math.min(1, Math.max(0.3, 3.9 / issueUnits)).toFixed(3));
  const publicationTitle = displayField(input.content, 'publication-title', 'こども日和');
  const publicationTitleFit = displayTextFit(publicationTitle.value, 5.2, 0.42);
  const publicationTitleGlyphs = Array.from(publicationTitle.value);
  const publicationTitleCut = Math.max(1, Math.ceil(publicationTitleGlyphs.length * 0.6));
  const publicationTitleMarkup = `<span>${publicationTitleGlyphs
    .slice(0, publicationTitleCut)
    .map((glyph) => esc(glyph))
    .join('')}</span><span>${publicationTitleGlyphs
    .slice(publicationTitleCut)
    .map((glyph) => esc(glyph))
    .join('')}</span>`;
  const publicationTagline = displayField(
    input.content,
    'publication-tagline',
    'わが子の今が、まるごと主役',
  );
  const publicationSubtitle = displayField(input.content, 'publication-subtitle', '記念号・非売品');
  const publicationDate = displayField(input.content, 'publication-date', input.publishedOn);
  const publicationDateSecondary = displayField(
    input.content,
    'publication-date',
    input.publishedOn,
    { secondary: true },
  );
  const leadKicker = displayField(input.content, 'lead-kicker', '今月のようす');
  const coverBadgeKicker = displayField(input.content, 'cover-badge-kicker', '成長記録特集');
  const coverBadgeTitle = displayField(input.content, 'cover-badge-title', '記念号外');
  const achievementsHeading = displayField(
    input.content,
    'achievements-heading',
    '今月のできるようになったこと',
  );
  const achievementsHeadingMarkup =
    achievementsHeading.value === '今月のできるようになったこと'
      ? '今月のできるように<br>なったこと'
      : achievementsHeading.html;
  const favoritesHeading = displayField(input.content, 'favorites-heading', '今月のお気に入り');
  const favoritesHeadingMarkup =
    favoritesHeading.value === '今月のお気に入り' ? '今月の<br>お気に入り' : favoritesHeading.html;
  const familyMessageHeading = displayField(
    input.content,
    'family-message-heading',
    '家族からのメッセージ',
  );
  const familyMessageSignature = displayField(
    input.content,
    'family-message-signature',
    '家族より',
  );
  const recordHeading = displayField(input.content, 'record-heading', '今月の記録');
  const recordPublicationDateLabel = displayField(
    input.content,
    'record-publication-date-label',
    '発行日',
  );
  const recordIssueLabel = displayField(input.content, 'record-issue-label', '今月号');
  const recordCoverLabel = displayField(input.content, 'record-cover-label', '表紙');
  const recordFeatureLabel = displayField(input.content, 'record-feature-label', '特集');
  const recordAchievementsLabel = displayField(
    input.content,
    'record-achievements-label',
    'できごと',
  );
  const recordFavoritesLabel = displayField(input.content, 'record-favorites-label', 'お気に入り');
  const topicsHeading = displayField(input.content, 'topics-heading', '今月のトピックス');
  const noteHeading = displayField(input.content, 'note-heading', '今月のひとこと');
  const bestShotHeading = displayField(input.content, 'best-shot-heading', '今月のベストショット');
  const recordNote = leadArticle?.caption ? coverCopy(leadArticle.caption, 36) : '';
  const recordRows: Array<{
    label: ResolvedDisplayField;
    value: string;
    articleIndex?: number;
    textField?: 'title';
    display?: ResolvedDisplayField;
  }> = [
    {
      label: recordPublicationDateLabel,
      value: publicationDateSecondary.value,
      display: publicationDateSecondary,
    },
    {
      label: recordIssueLabel,
      value: issueLabelSecondary.value,
      display: issueLabelSecondary,
    },
    {
      label: recordCoverLabel,
      value: shortTitle,
      ...(leadArticle ? { articleIndex: 0 } : {}),
    },
    {
      label: recordFeatureLabel,
      value: coverCopy(input.content.title, 16),
      textField: 'title',
    },
  ];
  if (achievementArticle) {
    recordRows.push({
      label: recordAchievementsLabel,
      value: coverCopy(achievementArticle.headline, 12),
      articleIndex: 1,
    });
  }
  if (favoriteArticle) {
    recordRows.push({
      label: recordFavoritesLabel,
      value: coverCopy(favoriteArticle.headline, 12),
      articleIndex: 2,
    });
  }
  const recordRowsMarkup = recordRows
    .map(
      ({ label, value, articleIndex, textField, display }) =>
        `<div${textField === 'title' ? ' data-tsuzuri-edit="title"' : articleIndex === undefined ? '' : ` data-tsuzuri-article-index="${articleIndex}"`}><dt${label.attributes}>${label.html}</dt><dd${display?.attributes ?? ''}>${display?.html ?? esc(value)}</dd></div>`,
    )
    .join('');
  const shoulderMat =
    'M9 0H91Q94 0 94 4V6H96Q100 7 100 12V88Q100 93 96 94H94V96Q94 100 91 100H9Q6 100 6 96V94H4Q0 93 0 88V12Q0 7 4 6H6V4Q6 0 9 0Z';
  const shoulderPaper =
    'M10 2H90Q92 2 92 4V7H94Q98 8 98 12V88Q98 92 94 93H92V96Q92 98 90 98H10Q8 98 8 96V93H6Q2 92 2 88V12Q2 8 6 7H8V4Q8 2 10 2Z';
  const cutMat = 'M4.3 0H95.7L100 4V96L95.7 100H4.3L0 96V4Z';
  const cutPaper = 'M5.3 1H94.7L98.6 4.6V95.4L94.7 99H5.3L1.4 95.4V4.6Z';
  const frameArt = {
    'achievement-ear': { className: 'is-shoulder', mat: shoulderMat, paper: shoulderPaper },
    'favorite-soft': { className: 'is-shoulder', mat: shoulderMat, paper: shoulderPaper },
    'message-letter': { className: 'is-shoulder', mat: shoulderMat, paper: shoulderPaper },
    'record-cut': { className: 'is-cut', mat: cutMat, paper: cutPaper },
    'topics-bracket': { className: 'is-shoulder', mat: shoulderMat, paper: shoulderPaper },
  } as const;
  const cardFrameMarkup = (variant: keyof typeof frameArt): string => {
    const art = frameArt[variant];
    return `<svg class="mag-card-frame ${art.className}" data-magazine-frame-art="${variant}" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true"><path class="mat" data-magazine-frame-layer="white-mat" d="${art.mat}"/><path class="paper" data-magazine-frame-layer="color-rule" d="${art.paper}"/></svg>`;
  };
  const bestCaption = coverCopy(
    leadArticle?.caption ?? input.images?.[1]?.alt ?? leadArticle?.headline ?? input.issueLabel,
    38,
  );
  const heroImage = input.images?.[0]
    ? { ...input.images[0], focalY: input.images[0].focalY ?? 36 }
    : undefined;
  const optionalCardPhoto = (index: number): string => {
    const image = input.images?.[index];
    return image
      ? `<figure class="mag-card-photo">${imageMarkup(image, 'photo')}<span aria-hidden="true"></span></figure>`
      : '';
  };
  const orphanPhotos = [
    achievementArticle ? undefined : input.images?.[2],
    favoriteArticle ? undefined : input.images?.[3],
  ].filter((image): image is RenderImage => Boolean(image));
  const orphanPhotosMarkup = orphanPhotos.length
    ? `<div class="mag-floating-cuts" data-magazine-region="additional-photos">${orphanPhotos
        .map((image) => `<figure>${imageMarkup(image, 'photo')}</figure>`)
        .join('')}</div>`
    : '';

  return `<main class="page magazine" data-magazine-layout="sunny-keepsake" style="--mag-name-fit:${shortTitleFit};--mag-logo-fit:${publicationTitleFit};--mag-growth-fit:${growthFit};--mag-issue-fit:${issueFit}">
    <div class="mag-sunwash" data-magazine-region="cover-background" aria-hidden="true"></div>

    <header class="mag-masthead" data-magazine-region="masthead">
      <p class="mag-tagline"${publicationTagline.attributes}>${publicationTagline.html}</p>
      <p class="mag-logo" data-magazine-region="logotype"${publicationTitle.attributes}>${publicationTitleMarkup}<i aria-hidden="true"></i></p>
      <div class="mag-issue" data-magazine-region="issue-line" data-overflow-check>
        <b${issueLabel.attributes}>${issueLabel.html}</b>
        <span${publicationSubtitle.attributes}>${publicationSubtitle.html}</span>
        <small${publicationDate.attributes}>${publicationDate.html}${publicationDate.authored ? '' : '発行'}</small>
      </div>
    </header>

    <section class="mag-headline"${leadArticle ? ' data-tsuzuri-article-index="0"' : ''} data-magazine-region="lead-feature" data-overflow-check>
      <p class="mag-ribbon"${leadKicker.attributes}>${leadKicker.html}</p>
      <h1 data-magazine-region="cover-headline" data-tsuzuri-edit="stories">${esc(shortTitle)}</h1>
      <h2 data-magazine-region="growth-headline" data-tsuzuri-edit="title">${growthMarkup}</h2>
      ${deck ? `<p class="mag-deck" data-tsuzuri-edit="stories">${esc(deck)}</p>` : ''}
    </section>

    <figure class="mag-hero" data-magazine-region="cover-photo" data-magazine-photo-footprint="dominant-framed" data-magazine-frame="asymmetric-arch-white">
      <div class="mag-hero-clip" data-magazine-frame-layer="photo-clip">${imageMarkup(heroImage)}</div>
    </figure>

    <aside class="mag-extra" data-magazine-region="growth-badge" data-magazine-frame="circle" data-overflow-check>
      <span${coverBadgeKicker.attributes}>${coverBadgeKicker.html}</span>
      <strong${coverBadgeTitle.attributes}>${coverBadgeTitle.html}</strong>
      <b data-tsuzuri-edit="title" data-tsuzuri-edit-target="secondary">${esc(coverCopy(input.content.title, 10))}</b>
      <small${issueLabelSecondary.attributes}>${issueLabelSecondary.html}</small>
    </aside>

    ${
      achievementArticle
        ? `<section class="mag-card mag-achievement" data-tsuzuri-article-index="1" data-magazine-card="achievements" data-magazine-frame="achievement-ear" data-magazine-padding="compact" data-magazine-type-scale="small" data-magazine-density="story-excerpt" data-tsuzuri-edit="stories" data-overflow-check>
      ${cardFrameMarkup('achievement-ear')}
      <h3${achievementsHeading.attributes}>${achievementsHeadingMarkup}</h3>
      ${articleExcerpt(achievementArticle, 'GROWING UP')}
      ${optionalCardPhoto(2)}
    </section>`
        : ''
    }

    ${
      favoriteArticle
        ? `<section class="mag-card mag-favorite" data-tsuzuri-article-index="2" data-magazine-card="favorites" data-magazine-frame="favorite-soft" data-magazine-padding="compact" data-magazine-type-scale="body" data-magazine-density="story-excerpt" data-tsuzuri-edit="stories" data-overflow-check>
      ${cardFrameMarkup('favorite-soft')}
      <h3${favoritesHeading.attributes}>${favoritesHeadingMarkup}</h3>
      ${articleExcerpt(favoriteArticle, 'OUR FAVORITES')}
      ${optionalCardPhoto(3)}
    </section>`
        : ''
    }

    ${orphanPhotosMarkup}

    ${featureStories.length ? `<section class="mag-feature-stories" data-magazine-region="feature-stories" aria-label="今月の特集">${featureStories.join('')}</section>` : ''}

    ${
      familyMessage
        ? `<section class="mag-card mag-message" data-tsuzuri-content-field="editorsNote" data-magazine-card="family-message" data-magazine-frame="message-letter" data-magazine-padding="roomy" data-magazine-type-scale="small" data-magazine-density="prose-8" data-tsuzuri-edit="stories" data-overflow-check>
      ${cardFrameMarkup('message-letter')}
      <h3${familyMessageHeading.attributes}>${familyMessageHeading.html}</h3>
      <div class="mag-message-body" data-magazine-region="message-body" data-magazine-layer="copy">
        <p data-magazine-region="message-copy" data-magazine-max-lines="8">${esc(familyMessage)}</p>
        <small data-magazine-region="message-signature" data-magazine-position="after-copy"${familyMessageSignature.attributes}>${familyMessageSignature.html}</small>
      </div>
      <span class="mag-sprig" data-magazine-decoration="botanical-sprig" data-magazine-layer="decoration-behind-copy" data-magazine-size="small" aria-hidden="true"><i></i><i></i><i></i><i></i><i></i><i></i><i></i></span>
    </section>`
        : ''
    }

    <section class="mag-card mag-record" data-tsuzuri-article-group="magazine-record" data-tsuzuri-article-group-label="${recordHeading.html}" data-magazine-card="monthly-record" data-magazine-frame="record-cut" data-magazine-padding="compact" data-magazine-type-scale="caption" data-magazine-density="table-4-6" data-tsuzuri-edit="stories" data-overflow-check>
      ${cardFrameMarkup('record-cut')}
      <h3${recordHeading.attributes}>${recordHeading.html}</h3>
      <dl>${recordRowsMarkup}</dl>
      ${recordNote ? `<p class="mag-record-note" data-tsuzuri-article-index="0" data-magazine-max-lines="2">${esc(recordNote)}</p>` : ''}
      <span class="mag-record-clover" aria-hidden="true"><i></i><i></i><i></i><i></i></span>
    </section>

    <section class="mag-card mag-topics" data-tsuzuri-article-group="magazine-topics" data-tsuzuri-article-group-label="${topicsHeading.html}" data-magazine-card="topics" data-magazine-frame="topics-bracket" data-magazine-padding="standard" data-magazine-type-scale="small" data-magazine-density="contents-3" data-tsuzuri-edit="stories" data-overflow-check>
      ${cardFrameMarkup('topics-bracket')}
      <h3${topicsHeading.attributes}>${topicsHeading.html}</h3>
      <ul>${topicItems}</ul>
      <span class="mag-topics-flower" aria-hidden="true">✿</span>
      <span class="mag-topics-camera" aria-hidden="true"><i></i></span>
    </section>

    ${
      note
        ? `<aside class="mag-note" data-tsuzuri-content-field="lead" data-magazine-region="monthly-note" data-magazine-frame="circle" data-tsuzuri-edit="stories" data-overflow-check>
      <span${noteHeading.attributes}>${noteHeading.html}</span>
      <strong>${esc(note)}</strong>
      <i class="mag-note-clover" aria-hidden="true"></i>
    </aside>`
        : ''
    }

    <figure class="mag-best-shot${input.images?.[1] ? '' : ' no-photo'}" data-magazine-region="best-shot" data-magazine-frame="polaroid" data-overflow-check>
      <h3${bestShotHeading.attributes}>${bestShotHeading.html}</h3>
      ${input.images?.[1] ? `<div data-magazine-frame-layer="photo-window">${imageMarkup(input.images[1], 'photo')}</div>` : '<div class="mag-camera" data-magazine-frame-layer="photo-window" aria-hidden="true"></div>'}
      <figcaption${leadArticle ? ' data-tsuzuri-article-index="0"' : ''} data-tsuzuri-edit-target="secondary" data-magazine-max-lines="2">${esc(bestCaption)}</figcaption>
      <span class="mag-polaroid-camera" aria-hidden="true"><i></i></span>
      <span class="mag-polaroid-heart" aria-hidden="true">♡</span>
    </figure>

    ${input.showBrand === false ? '' : '<div class="brand">きみの今を、未来のきみへ。</div>'}
  </main>`;
}

/** A portrait-led Japanese magazine cover with vertical display type and compact cover lines. */
function portraitMagazineCover(input: RenderIssueInput): string {
  const articles = layoutArticleSlots(input.content);
  const leftFeatures = articles
    .slice(0, 3)
    .flatMap((article, articleIndex) => (article ? [{ article, articleIndex }] : []));
  const circleArticle = articles[3];
  const esc = (value: string): string => escapeHtml(value);
  const copy = (value: string, maxChars: number): string =>
    clampCopy(value.replace(/\s+/gu, ' '), maxChars);
  // A closing Japanese quote is not the end of a cover line. The generic newspaper clamp
  // treats it as a sentence boundary, which used to drop the second half of lines such as
  // `小さな「できた！」が増えるころ` and left a conspicuous hole in the composition.
  const coverCopy = (value: string, maxChars: number): string => {
    const text = value.trim().replace(/\s+/gu, ' ');
    const glyphs = Array.from(text);
    if (glyphs.length <= maxChars) return text;
    return `${glyphs.slice(0, Math.max(1, maxChars - 1)).join('')}…`;
  };
  const compact = input.format === 'sns_4x5_jpeg' || input.format === 'postcard_jpeg';
  // Keep ordinary cover headlines intact; size them to the column before abbreviating.
  const mainHeadline = coverCopy(input.content.title, compact ? 16 : 18);
  const mainHeadlineFit = Number(
    Math.min(1, Math.max(0.42, 7.65 / Math.max(1, Array.from(mainHeadline).length))).toFixed(3),
  );
  const issueYear = /^(\d{4})年/u.exec(input.publishedOn)?.[1] ?? '';
  const issueMonth = Number(/年(\d{1,2})月/u.exec(input.publishedOn)?.[1] ?? '0');
  const season =
    issueMonth >= 3 && issueMonth <= 5
      ? '春'
      : issueMonth >= 6 && issueMonth <= 8
        ? '夏'
        : issueMonth >= 9 && issueMonth <= 11
          ? '秋'
          : '冬';
  const issueMark = /[春夏秋冬]号/u.exec(input.issueLabel)?.[0] ?? `${season}号`;
  const publicationTagline = displayField(
    input.content,
    'publication-tagline',
    '赤ちゃん・こどもの毎日をもっと楽しく',
  );
  const publicationTitle = displayField(input.content, 'publication-title', 'こども日和');
  const publicationTitleFit = displayTextFit(publicationTitle.value, 5.15, 0.42);
  const publicationTitleMarkup = Array.from(publicationTitle.value)
    .map((glyph) => `<span>${esc(glyph)}</span>`)
    .join('');
  const publicationSubtitle = displayField(input.content, 'publication-subtitle', 'ベビー こども');
  const publicationDate = displayField(input.content, 'publication-date', issueYear);
  const issueLabel = displayField(input.content, 'issue-label', issueMark);
  const volumeLabel = displayField(input.content, 'volume-label', 'Vol.1');
  const audienceLabel = displayField(input.content, 'audience-label', '0歳・1歳・2歳');
  const leftFeatureLabels = [
    displayField(input.content, 'left-feature-label-1', '今月の成長記録'),
    displayField(input.content, 'left-feature-label-2', 'お気に入りがきっと見つかる'),
    displayField(input.content, 'left-feature-label-3', '最高の一瞬を残そう'),
  ];
  const circleFeatureLabel = displayField(input.content, 'circle-feature-label', '今月の特集');
  const mainDeck = input.content.lead ? coverCopy(input.content.lead, 22) : '';
  const familyNote = input.content.editorsNote ? coverCopy(input.content.editorsNote, 18) : '';
  const heroImage = input.images?.[0]
    ? {
        ...input.images[0],
        focalX: input.images[0].focalX ?? 52,
        focalY: input.images[0].focalY ?? 44,
      }
    : undefined;
  const circleHeadlineLines = circleArticle
    ? wrapLines(coverCopy(circleArticle.headline, 10), 5, 2, 0.32)
    : [];
  const circleHeadlineFit = Number(
    Math.min(
      1,
      4.85 / Math.max(1, ...circleHeadlineLines.map((line) => Array.from(line.trim()).length)),
    ).toFixed(3),
  );
  const circleBody = circleArticle?.body ? copy(circleArticle.body, 28) : '';
  const familyNoteLines = familyNote ? wrapLines(familyNote, 9, 2, 0.4) : [];
  const leftFeatureMarkup = leftFeatures
    .map(({ article, articleIndex }, index) => {
      const featureHeadline = coverCopy(article.headline, 18);
      const featureHeadlineLines = wrapLines(featureHeadline, 10, 2, 0.32);
      const featureLineUnits = (line: string): number =>
        Array.from(line.trim()).reduce((total, glyph) => {
          if (/^[\p{P}\p{S}]$/u.test(glyph)) return total + 0.55;
          if (/^[\p{Script=Hiragana}\p{Script=Katakana}]$/u.test(glyph)) return total + 0.88;
          if (/^[A-Za-z0-9]$/u.test(glyph)) return total + 0.62;
          return total + 1;
        }, 0);
      const featureHeadlineFit = Number(
        Math.min(
          1,
          Math.max(0.86, 9.15 / Math.max(1, ...featureHeadlineLines.map(featureLineUnits))),
        ).toFixed(3),
      );
      const featureHeadlineMarkup = featureHeadlineLines
        .map((line) => `<b>${esc(line.trim())}</b>`)
        .join('');
      const featureLabel = leftFeatureLabels[index]!;
      return `<article class="pm-cover-line pm-cover-line-${index + 1}" data-tsuzuri-article-index="${articleIndex}" data-overflow-check style="--pm-feature-fit:${featureHeadlineFit}">
        <span${featureLabel.attributes}>${featureLabel.html}</span>
        <h3>${featureHeadlineMarkup}</h3>
        ${article.body ? `<p>${esc(coverCopy(article.body, 40))}</p>` : ''}
      </article>`;
    })
    .join('');

  const extraFeatures = articles.slice(4, 6).flatMap((article, index) =>
    article
      ? [
          `<article data-tsuzuri-article-index="${index + 4}" data-tsuzuri-edit="stories" data-overflow-check>
          <span aria-hidden="true">${index === 0 ? 'FAMILY JOURNAL' : 'LITTLE DISCOVERIES'}</span>
          <h3>${esc(coverCopy(article.headline, 20))}</h3>
          <p>${esc(coverCopy(article.body, 48))}</p>
        </article>`,
        ]
      : [],
  );

  return `<main class="page portrait-magazine" data-magazine-layout="portrait-feature" style="--pm-main-fit:${mainHeadlineFit};--pm-name-fit:${publicationTitleFit}">
    <figure class="pm-hero" data-magazine-region="cover-photo" data-magazine-photo-footprint="full-bleed-portrait">
      ${imageMarkup(heroImage)}
    </figure>
    <div class="pm-photo-wash" aria-hidden="true"></div>

    <header class="pm-masthead" data-magazine-region="masthead">
      <i class="pm-rays" aria-hidden="true"><b></b><b></b><b></b><b></b><b></b></i>
      <p class="pm-tagline"${publicationTagline.attributes}>${publicationTagline.html}</p>
      <h1 class="pm-masthead-title" aria-label="${publicationTitle.html}"${publicationTitle.attributes}>${publicationTitleMarkup}</h1>
      <p class="pm-masthead-sub"${publicationSubtitle.attributes}>${publicationSubtitle.html}</p>
      <i class="pm-flower" aria-hidden="true"><b></b></i>
    </header>

    <aside class="pm-issue" data-magazine-region="issue-line" data-overflow-check>
      ${publicationDate.value ? `<strong${publicationDate.attributes}>${publicationDate.html}</strong>` : ''}
      <b${issueLabel.attributes}>${issueLabel.html}</b>
      <span${volumeLabel.attributes}>${volumeLabel.html}</span>
    </aside>

    <p class="pm-pill" data-overflow-check${audienceLabel.attributes}>${audienceLabel.html}</p>

    <section class="pm-main-feature" data-magazine-region="lead-feature">
      <h2 class="pm-main-headline" data-text-flow="vertical" data-tsuzuri-edit="title" data-overflow-check>${esc(mainHeadline)}</h2>
      ${mainDeck ? `<p data-text-flow="vertical" data-tsuzuri-content-field="lead" data-tsuzuri-edit="stories" data-overflow-check>${esc(mainDeck)}</p>` : ''}
    </section>

    ${
      leftFeatureMarkup
        ? `<section class="pm-left-features" data-tsuzuri-article-group="portrait-left-features" data-tsuzuri-article-group-label="左の特集" data-tsuzuri-edit="stories" aria-label="左の特集">${leftFeatureMarkup}</section>`
        : ''
    }

    ${
      circleArticle || familyNote
        ? `<section class="pm-circle-feature" data-tsuzuri-article-group="portrait-circle-feature" data-tsuzuri-article-group-label="丸い特集" data-tsuzuri-edit="stories" data-overflow-check>
      <span${circleFeatureLabel.attributes}>${circleFeatureLabel.html}</span>
      ${
        circleArticle
          ? `<article data-tsuzuri-article-index="3" style="--pm-circle-fit:${circleHeadlineFit}"><h3>${circleHeadlineLines.map((line) => `<b>${esc(line.trim())}</b>`).join('')}</h3><i class="pm-book-rays" aria-hidden="true"><b></b><b></b><b></b><b></b></i>${!familyNote && circleBody ? `<p>${esc(circleBody)}</p>` : ''}</article>`
          : ''
      }
      ${familyNote ? `<blockquote data-tsuzuri-content-field="editorsNote">${familyNoteLines.map((line) => `<b>${esc(line)}</b>`).join('')}</blockquote>` : ''}
    </section>`
        : ''
    }

    ${extraFeatures.length ? `<section class="pm-extra-features" aria-label="家族の小さな特集">${extraFeatures.join('')}</section>` : ''}

    ${input.showBrand === false ? '' : '<div class="brand">きみの今を、未来のきみへ。</div>'}
  </main>`;
}

/** Four distinct photo-led covers, based on petlog's mode, natural, pop and journal series. */
function editorialSeriesCover(input: RenderIssueInput): string {
  const variant = input.template.replace('magazine-cover-', '');
  const settings = {
    mode: {
      title: 'LITTLE ME',
      tagline: 'その子らしさが、主役。',
      subtitle: 'A PORTRAIT OF YOU',
      count: 4,
    },
    weekend: {
      title: 'きみの休日',
      tagline: 'きみと、いつもの週末。',
      subtitle: 'weekend magazine',
      count: 4,
    },
    romantic: {
      title: 'Little Days',
      tagline: 'わが子への「だいすき」を、一冊に。',
      subtitle: 'LOVE, EVERY DAY.',
      count: 4,
    },
    journal: {
      title: 'きみ',
      tagline: '今のきみを、未来のきみへ。',
      subtitle: 'THE PORTRAIT ANNUAL',
      count: 2,
    },
  }[variant as 'mode' | 'weekend' | 'romantic' | 'journal']!;
  const crop = (value: string, max: number): string => {
    const chars = Array.from(value.trim().replace(/\s+/gu, ' '));
    return chars.length > max ? `${chars.slice(0, max - 1).join('')}…` : chars.join('');
  };
  const field = (id: DisplayFieldId, fallback: string) => displayField(input.content, id, fallback);
  const masthead = field('publication-title', settings.title);
  const tagline = field('publication-tagline', settings.tagline);
  const subtitle = field('publication-subtitle', settings.subtitle);
  const issue = field('issue-label', input.issueLabel);
  const date = field('publication-date', input.publishedOn);
  const headline =
    variant === 'mode' && input.content.title.includes('\n')
      ? input.content.title
          .split(/\n+/u)
          .map((line) => crop(line, 10))
          .slice(0, 5)
          .join('\n')
      : crop(input.content.title, variant === 'journal' ? 22 : 30);
  const stories = layoutArticleSlots(input.content)
    .slice(0, settings.count)
    .flatMap((article, index) =>
      article
        ? [
            `
    <article class="ec-story ec-story-${index}" data-tsuzuri-article-index="${index}" data-tsuzuri-edit="stories" data-overflow-check>
      <span class="ec-story-number" aria-hidden="true">${String(index + 1).padStart(2, '0')} / ${variant === 'journal' ? 'MEMORIES' : 'IN THIS ISSUE'}</span>
      <h3>${escapeHtml(crop(article.headline, variant === 'journal' ? 18 : 14))}</h3>
      <p>${escapeHtml(crop(article.body, variant === 'journal' ? 44 : 32))}</p>
    </article>`,
          ]
        : [],
    )
    .join('');
  return `<main class="page ec-cover ec-${variant}" style="--ec-name-fit:${displayTextFit(masthead.value, variant === 'journal' ? 3 : variant === 'weekend' ? 6 : variant === 'mode' ? 4.8 : 6.2, 0.12)};--ec-subtitle-fit:${displayTextFit(subtitle.value, 16, 0.35)};--ec-head-fit:${displayTextFit(headline, variant === 'journal' ? 12 : 22, 0.65)}">
    <figure class="ec-photo">${imageMarkup(input.images?.[0])}</figure>
    <div class="ec-wash" aria-hidden="true"></div><div class="ec-spine" aria-hidden="true"></div>
    <header class="ec-header">
      <p class="ec-tagline"${tagline.attributes} data-overflow-check>${tagline.html}</p>
      <h1 class="ec-masthead"${masthead.attributes} data-overflow-check>${masthead.html}</h1>
      <p class="ec-subtitle"${subtitle.attributes} data-overflow-check>${subtitle.html}</p>
    </header>
    <div class="ec-issue" data-overflow-check><span${issue.attributes}>${issue.html}</span><small${date.attributes}>${date.html}</small></div>
    <section class="ec-main"${variant === 'weekend' ? '' : ' data-overflow-check'}>
      <span class="ec-kicker" aria-hidden="true">${variant === 'journal' ? 'A LIFE, OBSERVED' : variant === 'romantic' ? 'OUR LITTLE STAR' : 'COVER STORY'}</span>
      <h2 data-tsuzuri-content-field="title" data-tsuzuri-edit="title" data-overflow-check>${
        variant === 'weekend'
          ? wrapLines(headline, Math.ceil(Array.from(headline).length / 2), 2, 0.35)
              .map((line) => `<span>${escapeHtml(line)}</span>`)
              .join('')
          : escapeHtml(headline)
      }</h2>
      ${input.content.lead ? `<p class="ec-lead" data-tsuzuri-content-field="lead" data-tsuzuri-edit="stories">${escapeHtml(crop(input.content.lead, 52))}</p>` : ''}
    </section>
    ${stories ? `<section class="ec-stories" aria-label="今号の特集">${stories}</section>` : ''}
    <span class="ec-ornament" aria-hidden="true">${variant === 'romantic' ? '♡' : variant === 'weekend' ? 'SMALL DAYS.\nBIG LOVE.' : variant === 'journal' ? 'PORTFOLIO\nNº 01' : 'Life, unfiltered.'}</span>
    ${input.content.editorsNote ? `<p class="ec-note" data-tsuzuri-content-field="editorsNote" data-tsuzuri-edit="editors-note" data-overflow-check>${escapeHtml(crop(input.content.editorsNote, 58))}</p>` : ''}
    ${input.showBrand === false ? '' : '<div class="brand">きみの今を、未来のきみへ。</div>'}
  </main>`;
}

function storybookPage(input: RenderIssueInput): string {
  const first = layoutArticleSlots(input.content)[0];
  const issueLabel = displayField(input.content, 'issue-label', input.issueLabel);
  const publicationDate = displayField(input.content, 'publication-date', input.publishedOn);
  // 絵本面の文字枠は判型の27%（A4 は縦が浅く実測で約8行）しかない。ここを clamp しないと
  // 長い月は「切られる」代わりに読了ゲートで render_failed になる。全文は本文ページにある。
  const storyLimit = input.format === 'a4_pdf' ? 300 : 540;
  const noteLimit = input.format === 'a4_pdf' ? 96 : 160;
  const storyDensity =
    (first?.body.length ?? 0) <= 200 && (input.content.editorsNote?.length ?? 0) <= 100
      ? 'short'
      : 'long';
  return `<main class="page storybook" data-story-density="${storyDensity}" style="--quiet-title-fit:${displayTextFit(input.content.title, 30, 0.32)}">
    <div class="story-orb orb-one"></div><div class="story-orb orb-two"></div>
    <p class="eyebrow"><span${issueLabel.attributes}>${issueLabel.html}</span> · <span${publicationDate.attributes}>${publicationDate.html}</span></p>
    <h1 class="headline" data-tsuzuri-edit="title" data-overflow-check>${escapeHtml(input.content.title)}</h1>
    <section class="story-photo">${imageMarkup(input.images?.[0])}</section>
    ${secondaryImagesMarkup(input.images, 'storybook-thumbs')}
    <section class="story-copy"${first ? ' data-tsuzuri-article-index="0"' : ''} data-tsuzuri-edit="stories" data-overflow-check>
      ${first ? `<h2>${escapeHtml(clampCopy(first.headline, 24))}</h2><p>${escapeHtml(clampCopy(first.body, storyLimit))}</p>` : ''}
      ${input.content.editorsNote ? `<blockquote data-tsuzuri-content-field="editorsNote">${escapeHtml(clampCopy(input.content.editorsNote, noteLimit))}</blockquote>` : ''}
    </section>
    ${input.showBrand === false ? '' : '<div class="brand">きみが主人公のものがたり。</div>'}
  </main>`;
}

function growthReport(input: RenderIssueInput): string {
  // 年表の各行は max-height 110px〜38mm の固定枠。storybook と同じ理由で clamp する。
  const entryLimit = 110;
  const articles = layoutArticleSlots(input.content);
  const growthDensity = articles
    .slice(0, 8)
    .every((article) => !article || (article.body.length <= 65 && article.headline.length <= 18))
    ? 'short'
    : 'long';
  const publicationTitle = displayField(input.content, 'publication-title', 'GROWTH REPORT');
  const issueLabel = displayField(input.content, 'issue-label', input.issueLabel);
  const publicationDate = displayField(input.content, 'publication-date', input.publishedOn);
  return `<main class="page growth" data-growth-density="${growthDensity}" style="--quiet-title-fit:${displayTextFit(input.content.title, 30, 0.32)}">
    <header><div><p class="eyebrow"><span${publicationTitle.attributes}>${publicationTitle.html}</span> · <span${issueLabel.attributes}>${issueLabel.html}</span></p><h1 class="headline" data-tsuzuri-edit="title" data-overflow-check>${escapeHtml(input.content.title)}</h1></div><time${publicationDate.attributes}>${publicationDate.html}</time></header>
    <section class="growth-grid">
      <div class="growth-visual"><div class="growth-photo">${imageMarkup(input.images?.[0])}</div>${secondaryImagesMarkup(input.images, 'growth-thumbs')}</div>
      <div class="timeline" data-tsuzuri-article-group="growth-timeline" data-tsuzuri-article-group-label="成長の記録">${articles
        .slice(0, 8)
        .flatMap((article, index) =>
          article
            ? [
                `<article data-tsuzuri-article-index="${index}" data-tsuzuri-edit="stories" data-overflow-check><span>${index + 1}</span><div><h2>${escapeHtml(clampCopy(article.headline, 26))}</h2><p>${escapeHtml(clampCopy(article.body, entryLimit))}</p></div></article>`,
              ]
            : [],
        )
        .join('')}</div>
    </section>
    ${input.content.lead ? `<footer data-tsuzuri-content-field="lead" data-tsuzuri-edit="stories" data-overflow-check>${escapeHtml(clampCopy(input.content.lead, 240))}</footer>` : ''}
    ${input.showBrand === false ? '' : '<div class="brand">きみのペースで、大きく。</div>'}
  </main>`;
}

const templateStyles: Record<IssueTemplate, string> = {
  'magazine-cover-mode': '',
  'magazine-cover-weekend': '',
  'magazine-cover-romantic': '',
  'magazine-cover-journal': '',

  'classic-newspaper': `
.newspaper{--ink:#11100d;--paper:#f6f2e9;--rule:rgba(17,16,13,.82);--hair:rgba(17,16,13,.34);--accent:#c91617;--leaf:#557b53;--np-scale:1;--np-spread-height:82.7%;--np-lower-height:13.2%;font-family:'Noto Serif JP Variable',serif;background:var(--paper);color:var(--ink);padding:calc(var(--safe) * .42);font-feature-settings:'palt' 0;text-spacing-trim:trim-start;print-color-adjust:exact}
.newspaper [data-text-flow='vertical']{writing-mode:vertical-rl;text-orientation:mixed;line-break:strict;word-break:normal}
.newspaper .np-tcy{text-combine-upright:all;font-feature-settings:'palt' 0}
.newspaper .np-photo-frame{min-width:0;min-height:0;overflow:hidden;background:#ddd7cb}
.newspaper .photo,.newspaper .empty-photo{width:100%;height:100%;filter:none}
.newspaper .empty-photo{font-size:calc(var(--page-w) * .0085 * var(--np-scale))}
.newspaper>.brand{right:calc(var(--safe) * .42);bottom:calc(var(--safe) * .08);color:#625c52;font-family:'Noto Serif JP Variable',serif;font-size:calc(var(--page-w) * .0057 * var(--np-scale));letter-spacing:.08em}

.newspaper .np-top-rail{height:2.35%;display:grid;grid-template-columns:1fr auto 1fr;align-items:start;gap:1em;padding:0 .2% .48%;border-bottom:1px solid var(--ink);font:600 calc(var(--page-w) * .014 * var(--np-scale))/1.15 'Noto Serif JP Variable',serif;letter-spacing:.055em;white-space:nowrap;overflow:hidden}
.newspaper .np-top-rail b{font-size:1.2em;letter-spacing:.2em}
.newspaper .np-top-rail span:last-child{text-align:right}

.newspaper .newspaper-spread{height:var(--np-spread-height);margin-top:1.05%;display:grid;grid-template-columns:minmax(0,84fr) minmax(0,15fr);gap:1.05%;overflow:hidden}
.newspaper .newspaper-main{min-width:0;min-height:0;display:grid;grid-template-rows:12.58fr 1.31fr 49.26fr 9.43fr 27.42fr;gap:0;overflow:hidden}
.newspaper .np-headline-banner{grid-row:1}
.newspaper .np-lead-photo{grid-row:3}
.newspaper .np-title-deck{grid-row:4}
.newspaper .np-editorial-band{grid-row:5}

/* 黒ベタは余計なラベルを置かず、主見出し一行だけで紙面の天を締める。 */
.newspaper .np-headline-banner{min-width:0;min-height:0;display:grid;place-items:center;padding:.8% 2.2%;background:var(--ink);color:#fff;overflow:hidden}
.newspaper .np-headline-banner h1{max-width:100%;margin:0;font:900 calc(var(--page-w) * .068 * var(--np-scale) * var(--np-title-fit,1))/1 'Noto Sans JP Variable',sans-serif;letter-spacing:.01em;text-align:center;white-space:nowrap;font-feature-settings:'palt' 0}

.newspaper .np-lead-photo{position:relative;min-width:0;min-height:0;margin:0;overflow:hidden;background:#ddd6c8}
.newspaper .np-lead-photo .np-photo-frame{width:100%;height:100%}
.newspaper .np-lead-photo figcaption{display:none}

.newspaper .np-title-deck{min-width:0;min-height:0;display:grid;grid-template-columns:minmax(0,1.55fr) minmax(0,.75fr);align-items:center;gap:2%;padding:0 2%;overflow:hidden}
.newspaper .np-title-deck h2{margin:0;font:700 calc(var(--page-w) * .075 * var(--np-scale) * var(--np-deck-fit,1))/1 'Noto Serif JP Variable',serif;letter-spacing:.03em;text-align:center;white-space:nowrap}
.newspaper .np-title-deck p{margin:0;font-size:calc(var(--page-w) * .035 * var(--np-scale));font-weight:600;letter-spacing:.14em;text-align:center;white-space:nowrap}

/* 中段は二つの箱と、罫を持たない縦組み記事。 */
.newspaper [data-newspaper-frame='double-inset']{position:relative;min-width:0;min-height:0;border:1px solid var(--rule);background:transparent}
.newspaper [data-newspaper-frame='double-inset']:after{content:'';position:absolute;inset:3px;border:1px solid var(--hair);pointer-events:none}
.newspaper [data-newspaper-frame='single-rule']{position:relative;min-width:0;min-height:0;border:1px solid var(--rule);background:transparent}
.newspaper [data-newspaper-frame='single-rule']:after{content:'';position:absolute;inset:2px;border:1px solid var(--hair);pointer-events:none}
.newspaper [data-newspaper-frame='corner-rule']{position:relative;min-width:0;min-height:0;border:1px solid var(--rule);background:transparent}
.newspaper [data-newspaper-frame]>*{position:relative;z-index:1}
.newspaper [data-newspaper-card] h3,.newspaper .np-rail-note h2{margin:0;padding-bottom:.3em;border-bottom:1px solid var(--hair);font-size:calc(var(--page-w) * .016 * var(--np-scale));font-weight:600;line-height:1.28;letter-spacing:.08em;text-align:center}
.newspaper .np-editorial-band{min-width:0;min-height:0;display:grid;grid-template-columns:30% 25% minmax(0,1fr);gap:2%;overflow:hidden}
.newspaper .np-message-card,.newspaper .np-news-card{padding:4.5% 7%;overflow:hidden}
.newspaper .np-message-card:before{content:'❧';position:absolute;z-index:2;right:7%;bottom:4%;color:var(--leaf);font-size:calc(var(--page-w) * .027 * var(--np-scale));transform:rotate(-13deg);opacity:.8}
.newspaper .np-message-card p{max-width:88%;margin:.62em 0 .35em;font-size:calc(var(--page-w) * .0135 * var(--np-scale));line-height:1.64;white-space:pre-wrap}
.newspaper .np-message-card small{display:block;margin-top:.35em;font-size:calc(var(--page-w) * .01 * var(--np-scale));font-weight:600;text-align:left;letter-spacing:.06em}
.newspaper .np-news-card h4{margin:.6em 0 .3em;font-size:calc(var(--page-w) * .0135 * var(--np-scale));font-weight:700;line-height:1.36;text-align:center}
.newspaper .np-news-card p{margin:0;font-size:calc(var(--page-w) * .013 * var(--np-scale));line-height:1.62;white-space:pre-wrap}
.newspaper .np-feature-story{min-width:0;min-height:0;padding:.8% 0 0 1.6%;overflow:hidden}
.newspaper .np-story-copy{width:100%;height:100%;font-size:calc(var(--page-w) * .0125 * var(--np-scale) * var(--np-story-fit,1));line-height:1.62;text-align:justify;overflow:hidden}
.newspaper .np-story-copy h3{margin:0 0 .5em .7em;font-size:2.15em;font-weight:700;line-height:1.3;letter-spacing:.025em}
.newspaper .np-story-copy p{margin:0;white-space:pre-wrap}

/* 下段は等幅コンポーネントにせず、記事・小欄・写真・短信で幅を変える。 */
.newspaper .np-lower-card-grid{height:var(--np-lower-height);min-width:0;min-height:0;margin-top:.7%;display:grid;grid-template-columns:minmax(0,26.5fr) minmax(0,23fr) minmax(0,23fr) minmax(0,26.5fr);gap:1.05%;padding-top:.12%;overflow:hidden}
.newspaper .np-milestones-card,.newspaper .np-small-news-card,.newspaper .np-best-shot-card,.newspaper .np-topics-card{margin:0;padding:2.8% 4.2%;overflow:hidden}
.newspaper .np-milestones-card h4,.newspaper .np-small-news-card h4{margin:.55em 0 .3em;font-size:calc(var(--page-w) * .0125 * var(--np-scale));line-height:1.4}
.newspaper .np-milestones-card p,.newspaper .np-small-news-card p{margin:0;font-size:calc(var(--page-w) * .0125 * var(--np-scale));line-height:1.5}
.newspaper .np-milestones-card:has(.np-card-photo-cut),.newspaper .np-small-news-card:has(.np-card-photo-cut){display:grid;grid-template-columns:minmax(0,1fr) 30%;grid-template-rows:auto auto minmax(0,1fr);column-gap:4%}
.newspaper .np-milestones-card:has(.np-card-photo-cut) h3,.newspaper .np-small-news-card:has(.np-card-photo-cut) h3{grid-column:1/-1}
.newspaper .np-card-photo-cut{position:relative;z-index:2;grid-column:2;grid-row:2/4;align-self:end;width:100%;height:58%;padding:2px;background:var(--paper);border:1px solid var(--hair);overflow:hidden}
.newspaper .np-best-shot-card{display:grid;grid-template-rows:auto minmax(0,1fr) auto;gap:.38em}
.newspaper .np-best-shot{min-width:0;min-height:0;display:grid;place-items:center;overflow:hidden;background:#e1dbcf}
.newspaper .np-best-shot>p{margin:0;padding:1em;font-size:calc(var(--page-w) * .011 * var(--np-scale));line-height:1.5;text-align:center}
.newspaper .np-best-shot-card figcaption{display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;font-size:calc(var(--page-w) * .0105 * var(--np-scale));font-weight:500;line-height:1.25;text-align:center;overflow:hidden}
.newspaper .np-topics-card ul{list-style:none;margin:.5em 0 0;padding:0;font-size:calc(var(--page-w) * .012 * var(--np-scale));line-height:1.38}
.newspaper .np-topics-card li{position:relative;padding:.27em 0 .27em 1.05em}
.newspaper .np-topics-card li:before{content:'・';position:absolute;left:0;font-weight:900}

/* 右袖は題字、発行情報、日付、朱赤の号外、横組みの一言を順に積む。 */
.newspaper .np-right-rail{min-width:0;min-height:0;display:grid;grid-template-rows:42fr 7fr 10fr 18fr 21fr;gap:.5%;overflow:hidden}
.newspaper .newspaper-masthead{position:relative;min-width:0;min-height:0;display:grid;place-items:center;border:2px solid var(--ink);padding:7%;overflow:hidden}
.newspaper .newspaper-masthead:after{content:'';position:absolute;inset:5px;border:1px solid var(--hair);pointer-events:none}
.newspaper .newspaper-masthead h1{height:100%;margin:0;font-size:calc(var(--page-w) * .064 * var(--np-scale) * var(--np-name-fit,1));font-weight:600;line-height:1;letter-spacing:.045em;text-orientation:upright;font-feature-settings:'palt' 0}
.newspaper .newspaper-masthead h1 span{display:block}
.newspaper .np-publisher{margin:0;display:grid;place-items:center;border-bottom:1px solid var(--rule);font-size:calc(var(--page-w) * .0103 * var(--np-scale));line-height:1.5;text-align:center;overflow:hidden}
.newspaper .np-masthead-meta{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.18em;border:1px solid var(--rule);font-size:calc(var(--page-w) * .0105 * var(--np-scale));font-weight:600;line-height:1.1;text-align:center;overflow:hidden}
.newspaper .np-masthead-meta time{font-size:calc(var(--page-w) * .036 * var(--np-scale));font-weight:700;font-variant-numeric:tabular-nums;letter-spacing:.02em}
.newspaper .np-masthead-meta b{font-size:1.08em;letter-spacing:.12em}
.newspaper .np-extra-edition{display:grid;grid-template-rows:minmax(0,1fr) auto;border:3px double var(--accent);background:var(--accent);color:#fff;text-align:center;overflow:hidden}
.newspaper .np-extra-edition strong{display:grid;grid-template-columns:auto auto;place-content:center;gap:.14em;font-size:calc(var(--page-w) * .046 * var(--np-scale));font-weight:700;line-height:1.02;letter-spacing:.06em}
.newspaper .np-extra-edition strong span{display:block}
.newspaper .np-extra-edition p{margin:0;padding:.45em .18em;background:var(--paper);color:var(--ink);font-size:calc(var(--page-w) * .011 * var(--np-scale));font-weight:600;line-height:1.4}
.newspaper .np-rail-note{display:grid;grid-template-rows:auto minmax(0,1fr) auto;gap:.55em;padding:6.5% 7.5%;overflow:hidden}
.newspaper .np-rail-note:before{content:'✤';position:absolute;right:8%;bottom:7%;color:var(--leaf);font-size:calc(var(--page-w) * .019 * var(--np-scale));transform:rotate(-9deg)}
.newspaper .np-rail-note h2{font-size:calc(var(--page-w) * .0145 * var(--np-scale));letter-spacing:.1em}
.newspaper .np-rail-note>p{align-self:start;margin:.6em 0 0;font-size:calc(var(--page-w) * .0125 * var(--np-scale));font-weight:500;line-height:1.62;text-align:center;white-space:pre-wrap;overflow:hidden}
.newspaper .np-rail-note>small{align-self:end;padding-bottom:1.3em;font-size:calc(var(--page-w) * .0092 * var(--np-scale));font-weight:600;line-height:1.45;text-align:center}
`,
  'horizontal-newspaper': `
.horizontal-newspaper{--hn-ink:#191713;--hn-rule:#353434;--hn-hair:#8c8983;--hn-soft:#d0d0d0;--hn-paper:#fefefd;--hn-green:#6f8d5a;--hn-scale:1;--hn-hairline:calc(var(--page-w) * .00095);--hn-row-teasers:9.3fr;--hn-row-lead:53.2fr;--hn-row-mid:12.5fr;--hn-row-lower:8.2fr;display:grid;grid-template-rows:2.2fr .8fr var(--hn-row-teasers) 1.3fr 9fr 1.2fr var(--hn-row-lead) .7fr var(--hn-row-mid) 1.6fr var(--hn-row-lower);padding:2.75% 4.25% 2%;border:var(--hn-hairline) solid var(--hn-rule);background:var(--hn-paper);color:var(--hn-ink);font-family:'Noto Serif JP Variable',serif;font-feature-settings:'palt' 0;text-spacing-trim:trim-start;line-break:strict;word-break:normal;print-color-adjust:exact}
.horizontal-newspaper.hn-no-teasers{--hn-row-teasers:0fr;--hn-row-lead:62.5fr}
.horizontal-newspaper.hn-no-mid{--hn-row-mid:0fr;--hn-row-lead:65.7fr}
.horizontal-newspaper.hn-no-teasers.hn-no-mid{--hn-row-lead:75fr}
.horizontal-newspaper [data-text-flow='horizontal']{writing-mode:horizontal-tb;text-orientation:mixed}
.horizontal-newspaper h1,.horizontal-newspaper h2,.horizontal-newspaper h3,.horizontal-newspaper p,.horizontal-newspaper figure,.horizontal-newspaper blockquote,.horizontal-newspaper dl,.horizontal-newspaper ul{margin:0}
.horizontal-newspaper p,.horizontal-newspaper li,.horizontal-newspaper dd,.horizontal-newspaper blockquote{overflow-wrap:anywhere}
.horizontal-newspaper .hn-folio{grid-row:1;min-width:0;display:grid;grid-template-columns:auto auto auto minmax(0,1fr);align-items:start;gap:2.5%;padding:0 .4% .4%;border-bottom:calc(var(--page-w) * .0047) double var(--hn-rule);font-size:calc(var(--page-w) * .0148 * var(--hn-scale));font-weight:500;line-height:1.12;letter-spacing:.025em;white-space:nowrap;overflow:hidden}
.horizontal-newspaper .hn-folio b{font-weight:600;letter-spacing:.08em}
.horizontal-newspaper .hn-folio span:last-child{min-width:0;padding-left:calc(var(--page-w) * .026);border-left:var(--hn-hairline) solid var(--hn-hair);overflow:hidden;text-overflow:clip}

.horizontal-newspaper .hn-top-teasers{grid-row:3;min-width:0;min-height:0;display:grid;grid-template-columns:1.03fr 1.04fr 1fr;border-bottom:calc(var(--page-w) * .0019) solid var(--hn-rule);overflow:hidden}
.horizontal-newspaper .hn-top-teasers[data-module-count='1']{grid-template-columns:1fr}
.horizontal-newspaper .hn-top-teasers[data-module-count='2']{grid-template-columns:1fr 1fr}
.horizontal-newspaper .hn-teaser{min-width:0;min-height:0;padding:1.15% 2.8%;display:block;border-right:calc(var(--page-w) * .0016) solid color-mix(in srgb,var(--hn-soft) 70%,var(--hn-paper));overflow:hidden}
.horizontal-newspaper .hn-teaser:last-child{border-right:0}
.horizontal-newspaper .hn-teaser.has-photo{display:grid;grid-template-columns:38% minmax(0,1fr);gap:5%}
.horizontal-newspaper .hn-teaser figure{min-width:0;min-height:0;height:94%;overflow:hidden;background:#e2dfd7}
.horizontal-newspaper .hn-teaser>div{min-width:0;min-height:0;display:flex;flex-direction:column;overflow:hidden}
.horizontal-newspaper .hn-teaser:not(.has-photo)>div{height:100%;padding-inline:1.5%}
.horizontal-newspaper .hn-teaser h2{display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;font-size:calc(var(--page-w) * .018 * var(--hn-scale));font-weight:600;line-height:1.22;letter-spacing:.025em;overflow:hidden}
.horizontal-newspaper .hn-teaser p{margin-top:.38em;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;font-size:calc(var(--page-w) * .0132 * var(--hn-scale));font-weight:400;line-height:1.48;overflow:hidden}
.horizontal-newspaper .hn-teaser:not(.has-photo) p{-webkit-line-clamp:3}
.horizontal-newspaper .hn-teaser small{margin-top:auto;font-size:calc(var(--page-w) * .012 * var(--hn-scale));font-weight:500;line-height:1.15;letter-spacing:.04em;text-align:right}

.horizontal-newspaper .hn-masthead-band{grid-row:5;min-width:0;min-height:0;display:grid;grid-template-columns:18% minmax(0,1fr) 14.5%;align-items:center;border-bottom:var(--hn-hairline) solid var(--hn-ink);overflow:hidden}
.horizontal-newspaper .hn-masthead-note{min-width:0;min-height:0;display:grid;grid-template-rows:56% 1fr;align-items:center;padding:3% 9%;overflow:hidden}
.horizontal-newspaper .hn-masthead-note i{position:relative;display:block;width:calc(var(--page-w) * .048);aspect-ratio:1;margin-left:2%;background:radial-gradient(circle,var(--hn-ink) 0 26%,transparent 27%),radial-gradient(circle,var(--hn-paper) 0 42%,transparent 43%),repeating-conic-gradient(from 0deg,var(--hn-ink) 0 4deg,transparent 4deg 45deg)}
.horizontal-newspaper .hn-masthead-note p{align-self:start;font-size:calc(var(--page-w) * .0126 * var(--hn-scale));font-weight:500;line-height:1.52;white-space:pre-wrap}
.horizontal-newspaper .hn-masthead-band>h1{min-width:0;font-size:calc(var(--page-w) * .096 * var(--hn-scale) * var(--hn-name-fit,1));font-weight:700;line-height:.95;letter-spacing:.105em;text-align:center;white-space:nowrap;overflow:hidden}
.horizontal-newspaper .hn-issue-meta{min-width:0;min-height:62%;display:flex;flex-direction:column;align-items:center;justify-content:center;border-left:var(--hn-hairline) solid var(--hn-hair);font-variant-numeric:tabular-nums;text-align:center;overflow:hidden}
.horizontal-newspaper .hn-issue-meta span{font-size:calc(var(--page-w) * .017 * var(--hn-scale));line-height:1.2}
.horizontal-newspaper .hn-issue-meta b{padding:.04em .25em .16em;border-bottom:var(--hn-hairline) solid var(--hn-hair);font-size:calc(var(--page-w) * .033 * var(--hn-scale));font-weight:600;line-height:1.12;white-space:nowrap}
.horizontal-newspaper .hn-issue-meta time{margin-top:.42em;font-size:calc(var(--page-w) * .0117 * var(--hn-scale));font-weight:500;white-space:nowrap}

.horizontal-newspaper .hn-lead-spread{grid-row:7;min-width:0;min-height:0;display:grid;grid-template-columns:21.6% minmax(0,1fr);gap:1.35%;overflow:hidden}
.horizontal-newspaper .hn-lead-spread.no-side{grid-template-columns:minmax(0,1fr);gap:0}
.horizontal-newspaper .hn-side-rail{min-width:0;min-height:0;padding-right:10.5%;display:grid;grid-template-rows:43fr 29fr 28fr;border-right:var(--hn-hairline) solid var(--hn-hair);overflow:hidden}
.horizontal-newspaper .hn-side-rail[data-module-count='1']{grid-template-rows:1fr}
.horizontal-newspaper .hn-side-rail[data-module-count='2']{grid-template-rows:55fr 45fr}
.horizontal-newspaper .hn-side-item{min-width:0;min-height:0;padding:2.1% 2.4% 3%;display:flex;flex-direction:column;border-bottom:var(--hn-hairline) solid var(--hn-hair);overflow:hidden}
.horizontal-newspaper .hn-side-item:last-child{border-bottom:0}
.horizontal-newspaper .hn-side-item h3{display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;font-size:calc(var(--page-w) * .0184 * var(--hn-scale));font-weight:600;line-height:1.24;letter-spacing:.02em;overflow:hidden}
.horizontal-newspaper .hn-side-item h3 span{display:block;margin:-.05em 0 .55em;padding:.08em .4em;background:var(--hn-ink);color:var(--hn-paper);font-size:.8em;font-weight:500;line-height:1.25;text-align:center;letter-spacing:.1em}
.horizontal-newspaper .hn-side-item p{margin-top:.5em;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:3;font-size:calc(var(--page-w) * .0127 * var(--hn-scale));line-height:1.55;overflow:hidden}
.horizontal-newspaper .hn-side-item:not(.has-photo) p{-webkit-line-clamp:5}
.horizontal-newspaper .hn-side-item figure{min-width:0;min-height:0;height:43%;margin-top:.8em;overflow:hidden;background:#dedad2}
.horizontal-newspaper .hn-side-item>small{margin-top:auto;font-size:calc(var(--page-w) * .012 * var(--hn-scale));font-weight:500;line-height:1.15;letter-spacing:.04em;text-align:right}
.horizontal-newspaper .hn-lead-story{min-width:0;min-height:0;display:grid;grid-template-rows:63.5fr 11fr 25.5fr;overflow:hidden}
.horizontal-newspaper .hn-lead-story.no-hero{grid-template-rows:18fr 82fr}
.horizontal-newspaper .hn-lead-story.no-hero>h1{font-size:calc(var(--page-w) * .066 * var(--hn-scale) * var(--hn-headline-fit,1));border-top:var(--hn-hairline) solid var(--hn-rule);border-bottom:var(--hn-hairline) solid var(--hn-rule)}
.horizontal-newspaper .hn-lead-story.no-hero .hn-lead-columns{align-content:start;padding-top:4%;grid-template-columns:repeat(3,minmax(0,1fr));gap:4%}
.horizontal-newspaper .hn-lead-story.no-hero .hn-lead-columns p{font-size:calc(var(--page-w) * .017 * var(--hn-scale));line-height:1.72}
.horizontal-newspaper .hn-lead-figure{min-width:0;min-height:0;display:grid;grid-template-rows:59.7fr 3.8fr;overflow:hidden}
.horizontal-newspaper .hn-lead-figure.no-caption{display:block}
.horizontal-newspaper .hn-lead-figure.no-caption .hn-lead-photo{height:100%}
.horizontal-newspaper .hn-lead-photo{min-width:0;min-height:0;overflow:hidden;background:#dedad2}
.horizontal-newspaper .hn-lead-caption{min-width:0;padding:.45em .25em 0;font-size:calc(var(--page-w) * .0104 * var(--hn-scale));font-weight:400;line-height:1.25;white-space:nowrap;overflow:hidden}
.horizontal-newspaper .hn-lead-story>h1{min-width:0;height:100%;display:flex;align-items:center;font-size:calc(var(--page-w) * .048 * var(--hn-scale) * var(--hn-headline-fit,1));font-weight:600;line-height:1;letter-spacing:.02em;white-space:nowrap;overflow:hidden}
.horizontal-newspaper .hn-lead-columns{min-width:0;min-height:0;display:grid;grid-template-columns:1fr 1.04fr 1.04fr;gap:4.9%;padding:1% .6% 0;overflow:hidden}
.horizontal-newspaper .hn-lead-columns p{min-width:0;min-height:0;font-size:calc(var(--page-w) * .0151 * var(--hn-scale));font-weight:400;line-height:1.52;letter-spacing:.025em;text-align:justify;text-indent:1em;overflow:hidden}

.horizontal-newspaper .hn-mid-grid{grid-row:9;min-width:0;min-height:0;width:calc(100% + var(--page-w) * .006);margin-left:calc(var(--page-w) * -.004);display:grid;grid-template-columns:22.8fr 25.1fr 26.4fr 25.7fr;border:var(--hn-hairline) solid var(--hn-hair);overflow:hidden}
.horizontal-newspaper .hn-mid-grid[data-module-count='1']{grid-template-columns:1fr}
.horizontal-newspaper .hn-mid-grid[data-module-count='2']{grid-template-columns:1fr 1fr}
.horizontal-newspaper .hn-mid-grid[data-module-count='3']{grid-template-columns:30fr 34fr 36fr}
.horizontal-newspaper .hn-mid-grid>article,.horizontal-newspaper .hn-mid-grid>figure{position:relative;min-width:0;min-height:0;padding:4.2% 6%;border-right:var(--hn-hairline) solid var(--hn-hair);overflow:hidden}
.horizontal-newspaper .hn-mid-grid>*:last-child{border-right:0}
.horizontal-newspaper .hn-mid-grid h2{margin-bottom:.45em;font-size:calc(var(--page-w) * .0163 * var(--hn-scale));font-weight:500;line-height:1.25;letter-spacing:.035em;text-align:center;white-space:nowrap}
.horizontal-newspaper .hn-mid-grid ul{padding:0;list-style:none;font-size:calc(var(--page-w) * .0127 * var(--hn-scale));line-height:1.58}
.horizontal-newspaper .hn-mid-grid li{position:relative;padding:.03em 0 .03em 1.2em;overflow:hidden}
.horizontal-newspaper .hn-mid-grid li:before{content:'・';position:absolute;left:0}
.horizontal-newspaper .hn-checklist li:before{content:'□';font-size:.82em}
.horizontal-newspaper .hn-family-message>div{position:relative;z-index:2;display:flex;min-height:0;flex-direction:column;align-items:flex-start}
.horizontal-newspaper .hn-family-message p{font-size:calc(var(--page-w) * .0125 * var(--hn-scale));line-height:1.58;white-space:pre-wrap}
.horizontal-newspaper .hn-family-message small{display:block;margin-top:.6em;font-size:calc(var(--page-w) * .0103 * var(--hn-scale));font-weight:500;line-height:1.3}
.horizontal-newspaper .hn-sprig-cut{position:absolute;z-index:1;right:3.5%;bottom:2%;width:20%;height:38%;color:var(--hn-green);opacity:.68;pointer-events:none}
.horizontal-newspaper .hn-sprig-cut path{fill:none;stroke:currentColor;stroke-width:1.7;stroke-linecap:round}
.horizontal-newspaper .hn-sprig-cut ellipse{fill:currentColor}
.horizontal-newspaper .hn-best-shot{display:grid;grid-template-rows:auto minmax(0,1fr) auto;gap:.35em}
.horizontal-newspaper .hn-best-shot h2:after{content:'';display:inline-block;width:1.05em;height:.72em;margin-left:.45em;background:radial-gradient(circle at 52% 57%,var(--hn-paper) 0 18%,var(--hn-ink) 20% 34%,transparent 36%),var(--hn-ink);clip-path:polygon(0 18%,24% 18%,31% 0,65% 0,73% 18%,100% 18%,100% 100%,0 100%);vertical-align:-.06em}
.horizontal-newspaper .hn-best-shot>div{min-width:0;min-height:0;overflow:hidden;background:#dedad2}
.horizontal-newspaper .hn-best-shot figcaption{display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;font-size:calc(var(--page-w) * .0108 * var(--hn-scale));font-weight:500;line-height:1.72;text-align:center;overflow:hidden}

.horizontal-newspaper .hn-lower-grid{grid-row:11;min-width:0;min-height:0;width:calc(100% + var(--page-w) * .006);margin-left:calc(var(--page-w) * -.004);display:grid;grid-template-columns:28.1fr 27.1fr 24.6fr 20.2fr;align-items:stretch;gap:1%;overflow:hidden}
.horizontal-newspaper .hn-lower-grid[data-module-count='1']{grid-template-columns:1fr}
.horizontal-newspaper .hn-lower-grid[data-module-count='2']{grid-template-columns:42fr 58fr}
.horizontal-newspaper .hn-lower-grid[data-module-count='3']{grid-template-columns:34fr 33fr 33fr}
.horizontal-newspaper .hn-lower-grid>article{position:relative;box-sizing:border-box;min-width:0;min-height:0;height:100%;margin-block:0;padding:3.5% 5%;align-self:stretch;overflow:hidden}
.horizontal-newspaper .hn-record{padding:.2% 5%!important;border:var(--hn-hairline) solid var(--hn-soft)}
.horizontal-newspaper .hn-watch-note{border:var(--hn-hairline) solid var(--hn-soft);overflow:visible!important}
.horizontal-newspaper .hn-watch-note h2{position:relative;display:inline-block;margin-top:calc(-1.08em + .25rem)!important;padding:0 .45em;background:var(--hn-paper)}
.horizontal-newspaper .hn-quote,.horizontal-newspaper .hn-editorial-note{border:var(--hn-hairline) solid var(--hn-soft);border-radius:calc(var(--page-w) * .002)}
.horizontal-newspaper .hn-lower-grid h2{margin-bottom:.32em;font-size:calc(var(--page-w) * .0147 * var(--hn-scale));font-weight:500;line-height:1.25;letter-spacing:.04em}
.horizontal-newspaper .hn-lower-grid p,.horizontal-newspaper .hn-lower-grid blockquote,.horizontal-newspaper .hn-lower-grid dt,.horizontal-newspaper .hn-lower-grid dd{font-size:calc(var(--page-w) * .0113 * var(--hn-scale));font-weight:400;line-height:1.66}
.horizontal-newspaper .hn-record h2{margin-bottom:.08em}
.horizontal-newspaper .hn-record dt,.horizontal-newspaper .hn-record dd{font-size:calc(var(--page-w) * .0105 * var(--hn-scale));line-height:1.28}
.horizontal-newspaper .hn-lower-grid blockquote{padding:.35em .2em;text-align:center}
.horizontal-newspaper .hn-lower-grid dl>div{display:grid;grid-template-columns:31% minmax(0,1fr);gap:.5em;border-bottom:var(--hn-hairline) solid var(--hn-soft)}
.horizontal-newspaper .hn-lower-grid dt{font-weight:600}
.horizontal-newspaper .hn-lower-grid dd{min-width:0;margin:0;white-space:nowrap;overflow:hidden}
.horizontal-newspaper .hn-quote h2,.horizontal-newspaper .hn-editorial-note h2{text-align:center}
.horizontal-newspaper .hn-watch-note p,.horizontal-newspaper .hn-quote blockquote,.horizontal-newspaper .hn-editorial-note p{position:relative;z-index:1}
.horizontal-newspaper .hn-watch-note p{padding-right:3%}
.horizontal-newspaper .hn-quote blockquote,.horizontal-newspaper .hn-editorial-note p{padding-right:12%}
.horizontal-newspaper .hn-editorial-cut{position:absolute;z-index:0;right:5%;bottom:7%;color:var(--hn-ink);opacity:.68;pointer-events:none}
.horizontal-newspaper .hn-editorial-cut path{fill:none;stroke:currentColor;stroke-width:1.7;stroke-linecap:round;stroke-linejoin:round}
.horizontal-newspaper .hn-pencil-cut{width:8%;height:42%;transform:rotate(7deg)}
.horizontal-newspaper .hn-leaf-cut{width:17%;height:45%;color:var(--hn-green)}
.horizontal-newspaper .hn-leaf-cut ellipse{fill:currentColor}
.horizontal-newspaper .hn-sun-cut{width:14%;height:40%}
.horizontal-newspaper .hn-sun-cut circle{fill:currentColor;stroke:none}
`,
  'magazine-cover': `
/* 4:5 を基準にした、明るい成長記録誌の表紙。共通グリッドではなく、写真と編集枠を
   わずかに重ねるコラージュで余白の強弱を作る。 */
.magazine{--mag-ink:#17170f;--mag-yellow:#fdd841;--mag-pink:#d64e6e;--mag-red:#e07081;--mag-green:#519056;--mag-blue:#3896d3;--mag-cream:#fffaf0;--mag-orange:#efb35a;--mag-scale:1;padding:0;background:var(--mag-yellow);color:var(--mag-ink);font-family:'Noto Sans JP Variable',sans-serif;font-feature-settings:'palt' 0;font-synthesis:none;text-spacing-trim:trim-start;print-color-adjust:exact}
.magazine .mag-sunwash{position:absolute;z-index:0;inset:0;background:radial-gradient(circle at 8% 12%,rgba(255,255,255,.24),transparent 21%),radial-gradient(circle at 84% 58%,rgba(255,243,139,.3),transparent 28%),repeating-radial-gradient(circle at 21% 33%,rgba(172,116,0,.035) 0 1px,transparent 1px 5px);mix-blend-mode:multiply;opacity:.72}

/* 題字はページ幅の八割強。手描き調の Mochiy Pop One を使い、右上の号情報は
   敢えて端へ寄せて題字との余白差を出す。 */
.magazine .mag-masthead{position:absolute;z-index:8;inset:0;pointer-events:none}
.magazine .mag-tagline{position:absolute;left:3.2%;top:1.25%;margin:0;font-size:calc(var(--page-w) * .0195 * var(--mag-scale));font-weight:700;line-height:1;letter-spacing:.28em;white-space:nowrap}
.magazine .mag-logo{position:absolute;left:2.6%;top:3.2%;width:84%;margin:0;display:flex;align-items:center;color:var(--mag-pink);font-family:'Mochiy Pop One',sans-serif;font-size:calc(var(--page-w) * .153 * var(--mag-scale) * var(--mag-logo-fit,1));font-weight:400;line-height:.9;letter-spacing:-.075em;white-space:nowrap}
.magazine .mag-logo span+span{margin-left:.04em}
.magazine .mag-logo i{position:relative;width:.56em;height:.56em;margin:.06em 0 0 .12em;border:.085em solid currentColor;border-radius:50%}
.magazine .mag-logo i:before,.magazine .mag-logo i:after{content:'';position:absolute;left:50%;top:50%;width:1.16em;height:.075em;background:linear-gradient(90deg,currentColor 0 18%,transparent 18% 82%,currentColor 82%);transform:translate(-50%,-50%)}
.magazine .mag-logo i:after{transform:translate(-50%,-50%) rotate(90deg)}
.magazine .mag-issue{position:absolute;right:.7%;top:1.6%;width:10.2%;display:flex;flex-direction:column;align-items:flex-start;padding-top:.18em;border-top:calc(var(--page-w) * .0018) solid var(--mag-ink);border-bottom:calc(var(--page-w) * .0011) solid var(--mag-ink);font-weight:700;line-height:1.3;text-align:left}
.magazine .mag-issue b{font-family:'Mochiy Pop One',sans-serif;font-size:calc(var(--page-w) * .023 * var(--mag-scale) * var(--mag-issue-fit,1));font-weight:400;white-space:nowrap}
.magazine .mag-issue span{font-size:calc(var(--page-w) * .0125 * var(--mag-scale));white-space:nowrap}
.magazine .mag-issue small{margin:.35em 0 .25em;font-size:calc(var(--page-w) * .0084 * var(--mag-scale));font-weight:700;line-height:1.35}

/* 左上の主見出し。白いリボン、黒い名前級、ピンクの成長見出しを詰め、本文だけに
   小さな呼吸を残す。 */
.magazine .mag-headline{position:absolute;z-index:9;left:3.3%;top:16.25%;width:69%;height:22.7%;overflow:visible}
.magazine .mag-ribbon{position:relative;width:max-content;min-width:31%;margin:0 0 -.06em;padding:.22em 1.15em .26em;background:var(--mag-cream);font-family:'Mochiy Pop One',sans-serif;font-size:calc(var(--page-w) * .0315 * var(--mag-scale));font-weight:400;line-height:1;letter-spacing:.08em;transform:rotate(-3deg)}
.magazine .mag-ribbon:before,.magazine .mag-ribbon:after{content:'';position:absolute;top:26%;width:1em;height:78%;background:var(--mag-cream);z-index:-1}
.magazine .mag-ribbon:before{left:-.7em;clip-path:polygon(0 0,100% 10%,82% 50%,100% 100%,0 88%,22% 50%)}
.magazine .mag-ribbon:after{right:-.72em;clip-path:polygon(0 10%,100% 0,78% 50%,100% 88%,0 100%,18% 50%)}
.magazine .mag-headline h1{width:max-content;max-width:99%;margin:.03em 0 0 .01em;font-family:'Mochiy Pop One',sans-serif;font-size:calc(var(--page-w) * .088 * var(--mag-scale) * var(--mag-name-fit,1));font-weight:400;line-height:1;letter-spacing:-.055em;white-space:nowrap;transform:rotate(-2.2deg);transform-origin:left center}
.magazine .mag-headline h2{width:99%;margin:.08em 0 0 .01em;color:var(--mag-pink);font-family:'Mochiy Pop One',sans-serif;font-size:calc(var(--page-w) * .061 * var(--mag-scale) * var(--mag-growth-fit,1));font-weight:400;line-height:1.02;letter-spacing:-.035em;transform:rotate(-6.4deg);transform-origin:left center}
.magazine .mag-headline h2 span{display:block;white-space:nowrap}
.magazine .mag-deck{max-width:52%;margin:.55em 0 0 .15em;font-size:calc(var(--page-w) * .0185 * var(--mag-scale));font-weight:700;line-height:1.58;letter-spacing:.09em}

/* 写真は背景ではなく、非対称に丸めた巨大な白縁カード。主要焦点は上三分の一に置く。 */
.magazine .mag-hero{position:absolute;z-index:2;left:13.7%;top:27.92%;width:75.5%;height:71.6%;margin:0;padding:calc(var(--page-w) * .009);background:#fffaf2;border-radius:86% 7% 3% 4%/29% 5% 2.5% 5%;box-shadow:0 calc(var(--page-w) * .003) calc(var(--page-w) * .008) rgba(139,91,5,.08);transform:rotate(-1deg);transform-origin:center;overflow:hidden}
.magazine .mag-hero-clip{width:100%;height:100%;border-radius:86.9% 6% 1.9% 2.9%/28.6% 4.1% 1.5% 4.1%;overflow:hidden;background:#f2e8d5}
.magazine .mag-hero .photo,.magazine .mag-hero .empty-photo{width:100%;height:100%;object-fit:cover}

/* 右上の円形号外はページの外へ少し裁ち落とし、整然としすぎない表紙のリズムを作る。 */
.magazine .mag-extra{position:absolute;z-index:10;right:-2.7%;top:12.55%;width:24.7%;aspect-ratio:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:calc(var(--page-w) * .035) calc(var(--page-w) * .025) calc(var(--page-w) * .012);background:#fdf2e1;border:calc(var(--page-w) * .008) solid #fff;border-radius:50%;box-shadow:none;text-align:center}
.magazine .mag-extra:after{content:'';position:absolute;inset:0;border:calc(var(--page-w) * .0014) dashed var(--mag-orange);border-radius:50%;pointer-events:none}
.magazine .mag-extra:before{content:'✦';position:absolute;z-index:3;right:5%;top:8%;color:var(--mag-orange);font-size:calc(var(--page-w) * .027 * var(--mag-scale));line-height:1;text-shadow:.42em .52em 0 color-mix(in srgb,var(--mag-orange) 66%,white);transform:rotate(8deg)}
.magazine .mag-extra>span{position:absolute;z-index:2;left:15%;right:15%;top:2%;padding:.35em .3em;background:var(--mag-pink);clip-path:polygon(0 12%,8% 0,92% 0,100% 12%,96% 88%,50% 100%,4% 88%);color:#fff;font-family:'Mochiy Pop One',sans-serif;font-size:calc(var(--page-w) * .0185 * var(--mag-scale));font-weight:400;letter-spacing:.06em;transform:rotate(-7deg)}
.magazine .mag-extra strong{position:relative;z-index:1;color:var(--mag-pink);font-family:'Mochiy Pop One',sans-serif;font-size:calc(var(--page-w) * .036 * var(--mag-scale));font-weight:400;line-height:1.08;letter-spacing:.03em}
.magazine .mag-extra b{position:relative;z-index:1;margin-top:.35em;color:var(--mag-pink);font-size:calc(var(--page-w) * .017 * var(--mag-scale));font-weight:900;line-height:1.35}
.magazine .mag-extra small{position:relative;z-index:1;margin-top:.45em;font-size:calc(var(--page-w) * .0105 * var(--mag-scale));font-weight:700}

/* 紙面の小枠は、左の耳付き罫と右の切り欠き罫を別々のSVGで描く。影はポラロイドに
   限り、カード本文は省略記号ではなく短い紙面行として組む。 */
.magazine .mag-card{--card-paper:#f8efe7;--frame-color:#df8792;position:absolute;z-index:7;padding:1.75% 2.05%;background:transparent;border:0;border-radius:0;box-shadow:none;overflow:hidden;isolation:isolate}
.magazine .mag-card[data-magazine-padding='compact']{padding:1.55% 1.85%}
.magazine .mag-card[data-magazine-padding='standard']{padding:1.75% 2.05%}
.magazine .mag-card[data-magazine-padding='roomy']{padding:1.8% 2.25%}
.magazine .mag-card-frame{position:absolute;z-index:0;inset:0;width:100%;height:100%;pointer-events:none;overflow:hidden}
.magazine .mag-card-frame path{vector-effect:non-scaling-stroke}
.magazine .mag-card-frame .mat{fill:rgba(255,255,255,.97);stroke:none}
.magazine .mag-card-frame .paper{fill:var(--card-paper);stroke:var(--frame-color);stroke-width:1.5}
.magazine .mag-card>*:not(.mag-card-frame):not(.mag-card-photo):not(.mag-sprig){position:relative;z-index:2}
.magazine .mag-card h3{margin:0 0 .35em;padding-bottom:.25em;border-bottom:1px solid color-mix(in srgb,var(--card-color,var(--mag-pink)) 35%,transparent);color:var(--card-color,var(--mag-pink));font-family:'Mochiy Pop One',sans-serif;font-size:calc(var(--page-w) * .0205 * var(--mag-scale));font-weight:400;line-height:1.25;letter-spacing:.04em;text-align:center}
.magazine .mag-card h4{margin:.55em 0 .25em;font-size:calc(var(--page-w) * .0135 * var(--mag-scale));font-weight:900;line-height:1.45}
.magazine .mag-card p,.magazine .mag-card li,.magazine .mag-card dt,.magazine .mag-card dd{font-size:calc(var(--page-w) * .0132 * var(--mag-scale));font-weight:600;line-height:1.62;letter-spacing:.02em}
.magazine .mag-card ul{margin:0;padding:0;list-style:none}
.magazine .mag-card li{position:relative;padding:.18em 0 .18em 1.05em}
.magazine .mag-card li:before{content:'☆';position:absolute;left:0;color:var(--card-color,var(--mag-pink));font-weight:900}
.magazine .mag-card-photo{position:absolute;z-index:2;right:4.5%;bottom:4%;width:16%;aspect-ratio:1;margin:0;padding:calc(var(--page-w) * .0022);background:#fff;border-radius:50%;box-shadow:0 calc(var(--page-w) * .001) calc(var(--page-w) * .003) rgba(91,64,24,.12);transform:rotate(-4deg)}
.magazine .mag-card-photo .photo{width:100%;height:100%;border-radius:50%;object-fit:cover}
.magazine .mag-card-photo span{position:absolute;right:-18%;top:-22%;width:auto;height:auto;background:none;transform:none}
.magazine .mag-card-photo span:before{content:'✦';color:var(--card-color,var(--mag-pink));font-size:calc(var(--page-w) * .012 * var(--mag-scale))}
.magazine .mag-floating-cuts{position:absolute;z-index:6;left:1.2%;top:43%;display:flex;flex-direction:column;gap:calc(var(--page-w) * .014)}
.magazine .mag-floating-cuts figure{width:calc(var(--page-w) * .15);aspect-ratio:1;margin:0;padding:calc(var(--page-w) * .006);background:#fff;box-shadow:0 calc(var(--page-w) * .003) calc(var(--page-w) * .01) rgba(94,63,13,.2);transform:rotate(-4deg);overflow:hidden}
.magazine .mag-floating-cuts figure:nth-child(2){margin-left:calc(var(--page-w) * .018);transform:rotate(5deg)}
.magazine .mag-floating-cuts .photo{width:100%;height:100%;object-fit:cover}
.magazine .mag-achievement{--card-color:var(--mag-red);--card-paper:#f8efe7;--frame-color:#df8792;left:.9%;top:39.6%;width:20.7%;height:calc(var(--page-w) * .224);padding:1.6% 1.8%;filter:drop-shadow(0 calc(var(--page-w) * .0018) calc(var(--page-w) * .0035) rgba(87,61,16,.08))}
.magazine .mag-favorite{--card-color:var(--mag-green);--card-paper:#e9ebe2;--frame-color:#83a68c;left:.8%;top:58.9%;width:20.8%;height:calc(var(--page-w) * .214);padding:1.65% 1.8%;filter:drop-shadow(0 calc(var(--page-w) * .0018) calc(var(--page-w) * .0035) rgba(87,61,16,.08))}
.magazine .mag-message{--card-color:var(--mag-red);--card-paper:#f6ede5;--frame-color:#e5a39f;left:.9%;top:77.5%;width:23.2%;height:calc(var(--page-w) * .276);padding:1.15% 2.15%;filter:drop-shadow(0 calc(var(--page-w) * .0018) calc(var(--page-w) * .0035) rgba(87,61,16,.08))}
.magazine .mag-achievement h3{font-size:calc(var(--page-w) * .0173 * var(--mag-scale));line-height:1.35}
.magazine .mag-favorite h3{font-size:calc(var(--page-w) * .0205 * var(--mag-scale))}
.magazine .mag-message h3{font-size:calc(var(--page-w) * .0176 * var(--mag-scale))}
.magazine .mag-achievement h3,.magazine .mag-favorite h3{padding-bottom:0;border-bottom:0}
.magazine .mag-message h3,.magazine .mag-record h3,.magazine .mag-topics h3{border-bottom-color:rgba(23,23,15,.22)}
.magazine .mag-achievement li{max-height:3.5em;padding:.32em 0 .32em 1.75em;font-size:calc(var(--page-w) * .0138 * var(--mag-scale));line-height:1.55;overflow:hidden}
.magazine .mag-favorite li{padding:.3em 0 .3em 2.35em;font-size:calc(var(--page-w) * .0137 * var(--mag-scale));line-height:1.55;white-space:nowrap;overflow:hidden;text-overflow:clip}
.magazine .mag-favorite li:before{content:'●';font-size:.72em}
.magazine .mag-favorite li:nth-child(2):before{content:'♪';font-size:1.05em}
.magazine .mag-favorite li:nth-child(3):before{content:'◆';font-size:.78em}
.magazine .mag-favorite li:nth-child(4):before{content:'▰';font-size:.78em}
.magazine .mag-message-body{position:relative;z-index:2;display:flex;min-height:0;flex-direction:column;align-items:flex-start}
.magazine .mag-message p{max-width:92%;margin:0;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:8;line-height:1.55;white-space:pre-wrap;overflow:hidden}
.magazine .mag-message small{position:relative;display:block;margin-top:.7em;font-size:calc(var(--page-w) * .0105 * var(--mag-scale));font-weight:700;line-height:1.4;white-space:nowrap}
.magazine .mag-sprig{position:absolute!important;z-index:1!important;right:4%;bottom:2%;width:24%;height:31%;overflow:hidden;pointer-events:none;transform:rotate(-3deg)}
.magazine .mag-sprig:before{content:'';position:absolute;left:42%;top:4%;bottom:-4%;width:2px;background:#78a58a;transform:rotate(34deg);transform-origin:bottom}
.magazine .mag-sprig:after{content:'';position:absolute;right:10%;bottom:13%;width:5%;aspect-ratio:1;background:#f2bf48;border-radius:50%;box-shadow:.55em -.35em 0 #f2bf48,-.45em .45em 0 #f2bf48}
.magazine .mag-sprig i{position:absolute;width:23%;aspect-ratio:1.7;background:#78add0;border-radius:80% 18% 80% 18%;transform:rotate(-24deg)}
.magazine .mag-sprig i:nth-child(1){left:22%;top:11%}
.magazine .mag-sprig i:nth-child(2){right:18%;top:22%;transform:rotate(47deg)}
.magazine .mag-sprig i:nth-child(3){left:11%;top:34%;transform:rotate(-31deg)}
.magazine .mag-sprig i:nth-child(4){right:4%;top:46%;transform:rotate(51deg)}
.magazine .mag-sprig i:nth-child(5){left:2%;top:59%;transform:rotate(-37deg)}
.magazine .mag-sprig i:nth-child(6){right:16%;top:68%;transform:rotate(45deg)}
.magazine .mag-sprig i:nth-child(7){left:22%;top:78%;transform:rotate(-33deg)}
.magazine .mag-record{--card-color:var(--mag-orange);--card-paper:#faf2e5;--frame-color:#efc786;right:-.25%;top:32.5%;width:25.8%;height:calc(var(--page-w) * .279);padding:1.15% 1.8%;filter:drop-shadow(0 calc(var(--page-w) * .001) calc(var(--page-w) * .002) rgba(87,61,16,.05))}
.magazine .mag-record h3{font-size:calc(var(--page-w) * .0145 * var(--mag-scale));letter-spacing:.08em}
.magazine .mag-record dl{margin:0}
.magazine .mag-record dl>div{display:grid;grid-template-columns:35% minmax(0,1fr);gap:.45em;padding:.28em 0;border-bottom:1px dotted rgba(23,23,15,.35)}
.magazine .mag-record dt{font-weight:900}
.magazine .mag-record dd{min-width:0;margin:0;text-align:right;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.magazine .mag-record dt,.magazine .mag-record dd{font-size:calc(var(--page-w) * .0118 * var(--mag-scale));line-height:1.48}
.magazine .mag-record-note{margin:.65em 0 0;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;font-size:calc(var(--page-w) * .0105 * var(--mag-scale))!important;line-height:1.55!important;overflow:hidden}
.magazine .mag-record-clover{position:absolute!important;z-index:3!important;right:4%;bottom:4%;width:16%;aspect-ratio:1;transform:rotate(-10deg)}
.magazine .mag-record-clover:after{content:'';position:absolute;left:49%;top:54%;width:8%;height:47%;background:#6e9f48;border-radius:999px;transform:rotate(35deg);transform-origin:top}
.magazine .mag-record-clover i{position:absolute;width:42%;aspect-ratio:1;background:#74a843;border-radius:62% 62% 55% 62%}
.magazine .mag-record-clover i:nth-child(1){left:8%;top:8%}.magazine .mag-record-clover i:nth-child(2){right:8%;top:8%}.magazine .mag-record-clover i:nth-child(3){left:8%;top:43%}.magazine .mag-record-clover i:nth-child(4){right:8%;top:43%}
.magazine .mag-topics{--card-color:var(--mag-blue);--card-paper:#eeefeb;--frame-color:#78b4d2;right:.25%;top:56.6%;width:25.2%;height:calc(var(--page-w) * .225);padding:1.5% 1.9%;filter:drop-shadow(0 calc(var(--page-w) * .001) calc(var(--page-w) * .002) rgba(87,61,16,.05))}
.magazine .mag-topics h3{font-size:calc(var(--page-w) * .0178 * var(--mag-scale))}
.magazine .mag-topics li{padding:.48em 0 .48em 1.15em;border-bottom:1px dotted rgba(23,23,15,.18);font-size:calc(var(--page-w) * .0132 * var(--mag-scale));line-height:1.48;white-space:nowrap;overflow:hidden;text-overflow:clip}
.magazine .mag-topics li:last-child{border-bottom:0}
.magazine .mag-topics li:before{content:'・'}
.magazine .mag-topics-flower{position:absolute!important;z-index:3!important;left:4%;bottom:1%;width:19%;height:23%;color:#a46bb8;font-size:calc(var(--page-w) * .038 * var(--mag-scale));line-height:1;overflow:hidden;transform:rotate(-6deg)}
.magazine .mag-topics-flower:after{content:'';position:absolute;left:52%;top:61%;width:2px;height:42%;background:#73a48b;transform:rotate(28deg);transform-origin:top}
.magazine .mag-topics-camera{position:absolute!important;z-index:3!important;right:5%;bottom:5%;width:17%;aspect-ratio:1.1;background:#efb53e;border:2px solid #5ca6c8;border-radius:45% 42% 36% 42%;transform:rotate(8deg)}
.magazine .mag-topics-camera:before{content:'';position:absolute;left:24%;top:21%;width:48%;aspect-ratio:1;background:#f8f3df;border:2px solid #5ca6c8;border-radius:50%}
.magazine .mag-topics-camera:after{content:'';position:absolute;left:38%;top:35%;width:22%;aspect-ratio:1;background:#5ca6c8;border-radius:50%}

/* 写真の上へ重ねる円形の一言とポラロイド。 */
.magazine .mag-note{position:absolute;z-index:9;left:38.1%;top:80.7%;width:22%;aspect-ratio:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:2.15%;background:#f7eee7;border:calc(var(--page-w) * .004) solid #fff;border-radius:50%;box-shadow:0 calc(var(--page-w) * .002) calc(var(--page-w) * .004) rgba(87,61,16,.08);text-align:center}
.magazine .mag-note:after{content:'';position:absolute;inset:0;border:calc(var(--page-w) * .00125) dashed var(--mag-orange);border-radius:50%;pointer-events:none}
.magazine .mag-note span{position:relative;z-index:1;margin-bottom:.55em;font-size:calc(var(--page-w) * .0125 * var(--mag-scale));font-weight:700}
.magazine .mag-note strong{position:relative;z-index:1;color:var(--mag-pink);font-family:'Mochiy Pop One',sans-serif;font-size:calc(var(--page-w) * .0205 * var(--mag-scale));font-weight:400;line-height:1.3}
.magazine .mag-note-clover{position:absolute;z-index:3;right:5%;bottom:4%;width:24%;aspect-ratio:1;background:radial-gradient(circle at 30% 30%,#78aa4e 0 20%,transparent 21%),radial-gradient(circle at 70% 30%,#78aa4e 0 20%,transparent 21%),radial-gradient(circle at 30% 70%,#78aa4e 0 20%,transparent 21%),radial-gradient(circle at 70% 70%,#78aa4e 0 20%,transparent 21%);transform:rotate(-12deg)}
.magazine .mag-note-clover:after{content:'';position:absolute;left:52%;top:62%;width:5%;height:50%;background:#6c9948;border-radius:999px;transform:rotate(32deg);transform-origin:top}
.magazine .mag-best-shot{position:absolute;z-index:9;right:2.8%;top:77.4%;width:25.9%;height:calc(var(--page-w) * .272);margin:0;padding:1.2% 1.35% 1.4%;display:grid;grid-template-rows:auto minmax(0,1fr) auto;gap:.5em;background:#faf5ed;box-shadow:0 calc(var(--page-w) * .003) calc(var(--page-w) * .009) rgba(94,63,13,.13);transform:rotate(6deg);overflow:hidden}
.magazine .mag-best-shot h3{margin:0;padding-right:17%;color:var(--mag-pink);font-family:'Mochiy Pop One',sans-serif;font-size:calc(var(--page-w) * .0215 * var(--mag-scale));font-weight:400;line-height:1.2;white-space:nowrap}
.magazine .mag-best-shot>div{min-height:0;overflow:hidden;background:#f1eadf}
.magazine .mag-best-shot .photo{width:100%;height:100%;object-fit:cover}
.magazine .mag-best-shot figcaption{display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;font-size:calc(var(--page-w) * .0152 * var(--mag-scale));font-weight:700;line-height:1.6;overflow:hidden}
.magazine .mag-polaroid-camera{position:absolute;z-index:3;right:3.5%;top:.7%;width:15%;aspect-ratio:1.28;background:var(--mag-ink);border-radius:8%;transform:rotate(-5deg)}
.magazine .mag-polaroid-camera:before{content:'';position:absolute;left:27%;top:20%;width:43%;aspect-ratio:1;border:calc(var(--page-w) * .0025) solid #fff;border-radius:50%}
.magazine .mag-polaroid-camera:after{content:'';position:absolute;left:13%;top:-14%;width:31%;height:20%;background:var(--mag-ink)}
.magazine .mag-polaroid-heart{position:absolute;z-index:3;right:2%;bottom:3%;color:var(--mag-pink);font-family:'Mochiy Pop One',sans-serif;font-size:calc(var(--page-w) * .022 * var(--mag-scale));font-weight:400;line-height:1;transform:rotate(-12deg)}
.magazine .mag-camera{position:relative;width:35%;aspect-ratio:1.28;margin:auto;background:var(--mag-ink);border-radius:8%}
.magazine .mag-camera:before{content:'';position:absolute;left:29%;top:19%;width:42%;aspect-ratio:1;border:calc(var(--page-w) * .006) solid white;border-radius:50%}
.magazine .mag-camera:after{content:'';position:absolute;left:12%;top:-13%;width:30%;height:18%;background:var(--mag-ink)}
.magazine>.brand{z-index:11;right:30.5%;bottom:.25%;color:rgba(23,23,15,.66);font-size:calc(var(--page-w) * .0075 * var(--mag-scale))}
`,
  'magazine-cover-portrait': `
/* 添付された4:5誌面を基準グリッドにする。写真は上端から続く一枚で、白い帯は作らない。 */
.portrait-magazine{--pm-pink:#ed3f78;--pm-yellow:#ffe33d;--pm-ink:#171514;--pm-paper:#fbfaf6;padding:0;background:var(--pm-paper);color:var(--pm-ink);font-family:'Noto Sans JP Variable',sans-serif;font-synthesis:none}
.portrait-magazine .pm-hero{position:absolute;z-index:0;inset:0;margin:0;overflow:hidden;background:#f1efe9}
.portrait-magazine .pm-hero .photo,.portrait-magazine .pm-hero .empty-photo{width:100%;height:100%;object-fit:cover}
.portrait-magazine .pm-photo-wash{position:absolute;z-index:1;inset:0;pointer-events:none;background:linear-gradient(180deg,rgba(251,250,246,.9) 0,rgba(251,250,246,.82) 10.5%,rgba(251,250,246,.38) 15%,rgba(251,250,246,.09) 18.8%,rgba(251,250,246,0) 22%),linear-gradient(90deg,rgba(251,250,246,.38) 0,rgba(251,250,246,.22) 8%,rgba(251,250,246,.08) 16%,rgba(251,250,246,0) 25%),linear-gradient(270deg,rgba(251,250,246,.18) 0,rgba(251,250,246,.05) 8%,rgba(251,250,246,0) 14%)}

/* 題字は実測の x=6.5%, y=4.4%, w=77.5%, h=13.4% を基準に、一字ずつ揺らす。 */
.portrait-magazine .pm-masthead{position:absolute;z-index:6;inset:0;pointer-events:none}
.portrait-magazine .pm-rays{position:absolute;left:3.5%;top:1.55%;width:9.8%;height:5.35%}
.portrait-magazine .pm-rays b{position:absolute;left:50%;top:50%;width:31%;height:10%;border-radius:999px;background:var(--pm-pink);transform-origin:0 50%}
.portrait-magazine .pm-rays b:nth-child(1){transform:translate(-48%,-50%) rotate(196deg) translateX(42%)}.portrait-magazine .pm-rays b:nth-child(2){transform:translate(-48%,-50%) rotate(222deg) translateX(53%)}.portrait-magazine .pm-rays b:nth-child(3){transform:translate(-48%,-50%) rotate(254deg) translateX(52%)}.portrait-magazine .pm-rays b:nth-child(4){transform:translate(-48%,-50%) rotate(286deg) translateX(53%)}.portrait-magazine .pm-rays b:nth-child(5){transform:translate(-48%,-50%) rotate(316deg) translateX(46%)}
.portrait-magazine .pm-tagline{position:absolute;left:19.2%;top:2.35%;margin:0;font-size:calc(var(--page-w) * .02);font-weight:800;line-height:1;letter-spacing:.035em;white-space:nowrap}
.portrait-magazine .pm-masthead-title{position:absolute;left:6.51%;top:4.42%;width:77.54%;height:13.41%;display:flex;align-items:center;margin:0;color:var(--pm-pink);font-family:'Mochiy Pop One','Noto Sans JP Variable',sans-serif;font-size:calc(var(--page-w) * .164 * var(--pm-name-fit,1));font-weight:400;line-height:.88;letter-spacing:-.08em;white-space:nowrap}
.portrait-magazine .pm-masthead-title span{display:inline-block;transform-origin:50% 62%}.portrait-magazine .pm-masthead-title span:nth-child(1){font-size:1.04em;transform:translateY(-.02em) rotate(-5deg)}.portrait-magazine .pm-masthead-title span:nth-child(2){font-size:.92em;transform:translateY(.045em) rotate(4deg)}.portrait-magazine .pm-masthead-title span:nth-child(3){font-size:1.01em;transform:translateY(-.005em) rotate(-2deg)}.portrait-magazine .pm-masthead-title span:nth-child(4){font-size:.96em;transform:translateY(.025em) rotate(-3deg)}.portrait-magazine .pm-masthead-title span:nth-child(5){font-size:.98em;transform:translateY(.05em) rotate(3deg)}
.portrait-magazine .pm-masthead-sub{position:absolute;left:15.45%;top:18.68%;margin:0;color:var(--pm-pink);font-size:calc(var(--page-w) * .026);font-weight:800;line-height:1;letter-spacing:.055em;white-space:nowrap}
.portrait-magazine .pm-flower{position:absolute;left:78.3%;top:14.3%;width:4.9%;aspect-ratio:1;transform:rotate(10deg)}
.portrait-magazine .pm-flower:before,.portrait-magazine .pm-flower:after,.portrait-magazine .pm-flower b:before,.portrait-magazine .pm-flower b:after{content:'';position:absolute;border:calc(var(--page-w) * .0022) solid var(--pm-pink);background:transparent}
.portrait-magazine .pm-flower:before{left:31%;top:-2%;width:38%;height:47%;border-radius:55% 55% 46% 46%;transform:rotate(-3deg)}.portrait-magazine .pm-flower:after{left:31%;bottom:-2%;width:38%;height:47%;border-radius:46% 46% 55% 55%;transform:rotate(3deg)}
.portrait-magazine .pm-flower b{position:absolute;left:40%;top:40%;width:20%;height:20%;border:calc(var(--page-w) * .0022) solid var(--pm-pink);border-radius:50%}.portrait-magazine .pm-flower b:before{right:42%;top:-87%;width:230%;height:190%;border-radius:55% 46% 46% 55%;transform:rotate(-4deg)}.portrait-magazine .pm-flower b:after{left:42%;top:-87%;width:230%;height:190%;border-radius:46% 55% 55% 46%;transform:rotate(4deg)}

.portrait-magazine .pm-issue{position:absolute;z-index:7;right:4.2%;top:4.1%;width:7.7%;height:11%;font-family:'Noto Sans JP Variable',sans-serif;font-variant-numeric:lining-nums proportional-nums;line-height:1.05;text-align:center}
.portrait-magazine .pm-issue:before,.portrait-magazine .pm-issue:after{content:'';position:absolute;left:0;width:100%;border-top:calc(var(--page-w) * .0024) dotted var(--pm-ink)}.portrait-magazine .pm-issue:before{top:0}.portrait-magazine .pm-issue:after{top:69%}
.portrait-magazine .pm-issue strong,.portrait-magazine .pm-issue b,.portrait-magazine .pm-issue span{position:absolute;left:0;width:100%}.portrait-magazine .pm-issue strong{top:14%;font-size:calc(var(--page-w) * .0245);font-weight:800;letter-spacing:.02em}.portrait-magazine .pm-issue b{top:38%;font-size:calc(var(--page-w) * .0285);font-weight:800;line-height:1.08}.portrait-magazine .pm-issue span{top:76%;font-size:calc(var(--page-w) * .027);font-weight:800;line-height:1}
.portrait-magazine .pm-pill{position:absolute;z-index:8;left:79.5%;top:19.12%;width:18.36%;height:2.92%;display:flex;align-items:center;justify-content:center;margin:0;border-radius:999px;background:var(--pm-pink);color:#fff;font-size:calc(var(--page-w) * .0215);font-weight:900;line-height:1;letter-spacing:.015em;white-space:nowrap}

/* 右の二本はページ全体の座標で管理し、26px相当の呼吸を固定する。 */
.portrait-magazine .pm-main-feature{position:absolute;z-index:5;left:80.1%;top:26.03%;width:14.8%;height:62.5%}
.portrait-magazine .pm-main-feature h2{position:absolute;right:0;top:0;width:66%;height:100%;margin:0;font-family:'Noto Serif JP Variable',serif;font-size:calc(var(--page-w) * .092 * var(--pm-main-fit,1));font-weight:650;line-height:1;letter-spacing:.105em;white-space:nowrap;writing-mode:vertical-rl;text-orientation:upright;font-feature-settings:'vert' 1,'vrt2' 1,'palt' 0;font-kerning:none}
.portrait-magazine .pm-main-feature>p{position:absolute;left:0;top:5%;width:22%;height:78%;margin:0;color:var(--pm-pink);font-size:calc(var(--page-w) * .0258);font-weight:900;line-height:1;letter-spacing:.035em;writing-mode:vertical-rl;text-orientation:upright;font-feature-settings:'vert' 1,'vrt2' 1,'palt' 0}

/* 左の三段はカードにせず、写真の余白へ直接組む。 */
.portrait-magazine .pm-left-features{position:absolute;z-index:5;left:2.94%;top:37.73%;width:22.8%;height:31.6%}
.portrait-magazine .pm-cover-line{position:absolute;left:0;width:100%;min-height:0}.portrait-magazine .pm-cover-line-1{top:0;height:44%}.portrait-magazine .pm-cover-line-2{top:46.1%;height:27%}.portrait-magazine .pm-cover-line-3{top:77.8%;height:27%}
.portrait-magazine .pm-cover-line>span{display:inline-block;margin:0 0 calc(var(--page-w) * .0105);padding:.1em .3em .14em;background:var(--pm-yellow);font-size:calc(var(--page-w) * .0175);font-weight:900;line-height:1.05;letter-spacing:0;white-space:nowrap}
.portrait-magazine .pm-cover-line-2>span{background:var(--pm-pink);color:#fff;font-size:calc(var(--page-w) * .0158);letter-spacing:-.025em}.portrait-magazine .pm-cover-line-3>span{background:var(--pm-yellow)}
.portrait-magazine .pm-cover-line h3{margin:0;font-size:calc(var(--page-w) * .0245 * var(--pm-feature-fit,1));font-weight:900;line-height:1.2;letter-spacing:-.01em;font-feature-settings:'palt' 1;font-kerning:normal}.portrait-magazine .pm-cover-line h3 b{display:block;font-weight:900}
.portrait-magazine .pm-cover-line p{margin:.48em 0 0;font-size:calc(var(--page-w) * .0168);font-weight:700;line-height:1.38;letter-spacing:.005em;white-space:pre-wrap}

.portrait-magazine .pm-circle-feature{position:absolute;z-index:7;left:2.32%;bottom:4.14%;width:30.04%;aspect-ratio:1;border-radius:50%;background:var(--pm-yellow);text-align:center}
.portrait-magazine .pm-circle-feature>span{position:absolute;left:23.8%;top:11.7%;width:51.7%;height:13.2%;display:flex;align-items:center;justify-content:center;background:var(--pm-pink);clip-path:polygon(4% 10%,96% 0,100% 88%,50% 100%,0 90%);color:#fff;font-family:'M PLUS Rounded 1c','Noto Sans JP Variable',sans-serif;font-size:calc(var(--page-w) * .0195);font-weight:900;line-height:1;letter-spacing:.055em;transform:rotate(-2deg)}
.portrait-magazine .pm-circle-feature article{position:absolute;left:14.8%;top:27.8%;width:70.4%;height:63%}.portrait-magazine .pm-circle-feature h3{margin:0;color:var(--pm-pink);font-family:'M PLUS Rounded 1c','Noto Sans JP Variable',sans-serif;font-size:calc(var(--page-w) * .043 * var(--pm-circle-fit,1));font-weight:900;line-height:1.06;letter-spacing:.01em}.portrait-magazine .pm-circle-feature h3 b{display:block;font-weight:900}.portrait-magazine .pm-circle-feature h3 b:nth-child(2){letter-spacing:.06em}.portrait-magazine .pm-circle-feature article p{position:absolute;left:5%;right:5%;bottom:0;margin:0;font-family:'Noto Sans JP Variable',sans-serif;font-size:calc(var(--page-w) * .018);font-weight:800;line-height:1.42}
.portrait-magazine .pm-circle-feature blockquote{position:absolute;left:16%;bottom:14%;width:68%;margin:0;padding:0;border:0;font-family:'Noto Sans JP Variable',sans-serif;font-size:calc(var(--page-w) * .018);font-weight:800;line-height:1.42}.portrait-magazine .pm-circle-feature blockquote b{display:block;font-weight:800;white-space:nowrap}
.portrait-magazine .pm-book-rays{position:absolute;left:0;top:37%;width:100%;height:18%;pointer-events:none}.portrait-magazine .pm-book-rays b{position:absolute;width:10%;height:8%;border-radius:999px;background:var(--pm-pink);transform-origin:center}.portrait-magazine .pm-book-rays b:nth-child(1){left:-1%;top:20%;transform:rotate(21deg)}.portrait-magazine .pm-book-rays b:nth-child(2){left:1%;top:67%;transform:rotate(-18deg)}.portrait-magazine .pm-book-rays b:nth-child(3){right:-1%;top:20%;transform:rotate(-21deg)}.portrait-magazine .pm-book-rays b:nth-child(4){right:1%;top:67%;transform:rotate(18deg)}
.portrait-magazine>.brand{right:2%;bottom:1%;color:rgba(23,21,20,.64);font-size:calc(var(--page-w) * .0082);letter-spacing:.08em}
`,
  'storybook-page': `
.storybook{background:#f8eee5;color:#51483f;padding:calc(var(--safe) * 1.25)}.storybook .story-orb{position:absolute;border-radius:50%;filter:blur(1px);opacity:.7}.storybook .orb-one{width:45%;aspect-ratio:1;right:-12%;top:-10%;background:#e7cdbb}.storybook .orb-two{width:25%;aspect-ratio:1;left:-8%;bottom:3%;background:#c9d9c3}.storybook>.eyebrow{position:relative}.storybook h1{position:relative;font:700 clamp(30px,6vw,78px)/1.25 'Noto Serif JP Variable',serif;max-width:78%;margin:10px 0 20px}.storybook .story-photo{position:relative;width:75%;height:47%;margin-left:auto;border-radius:48% 48% 12px 12px/16% 16% 12px 12px;overflow:hidden;box-shadow:12px 16px 0 #ead8c8}.storybook .storybook-thumbs{position:absolute;z-index:2;left:var(--safe);top:35%;display:grid;gap:8px}.storybook .secondary-photo{width:clamp(54px,11vw,125px);aspect-ratio:1;border-radius:50%;border:3px solid #f8eee5;box-shadow:3px 5px 0 #ead8c8}.storybook .story-copy{position:relative;width:76%;margin-top:6%;max-height:27%;font-family:'Noto Serif JP Variable',serif}.storybook .story-copy h2{font-size:clamp(18px,3vw,38px);margin:0 0 8px}.storybook .story-copy p{line-height:2}.storybook blockquote{border-left:2px solid #bd6b56;margin:12px 0;padding-left:12px;font-size:11px}
`,
  'growth-report': `
.growth{background:#f5f3ed;color:#25332f}.growth header{display:flex;justify-content:space-between;align-items:end;border-bottom:3px solid #45645b;padding-bottom:12px}.growth h1{font-size:clamp(30px,6vw,76px)}.growth time{font-size:10px}.growth-grid{display:grid;grid-template-columns:40% 1fr;gap:6%;height:72%;margin-top:6%}.growth-visual{min-width:0}.growth-photo{height:58%;border-radius:50% 50% 8px 8px/18% 18% 8px 8px;overflow:hidden;box-shadow:10px 10px 0 #d7a27e}.growth-thumbs{display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin-top:18px}.growth .secondary-photo{aspect-ratio:1;border-radius:8px}.timeline{position:relative;padding-left:16px}.timeline:before{content:'';position:absolute;left:13px;top:10px;bottom:10px;width:2px;background:#a6b8ae}.timeline article{position:relative;display:grid;grid-template-columns:30px 1fr;gap:10px;max-height:110px;margin-bottom:14px}.timeline article>span{z-index:1;display:grid;place-items:center;width:28px;height:28px;border-radius:50%;background:#45645b;color:#fff;font-weight:700}.timeline h2{font-size:clamp(14px,2.3vw,27px);margin:0 0 3px}.timeline p{font-size:clamp(9px,1.15vw,14px);line-height:1.65}.growth footer{position:absolute;left:var(--safe);bottom:calc(var(--safe) * 1.4);max-width:66%;font:600 13px/1.7 'Noto Serif JP Variable',serif}
`,
};

/** Each output has its own composition; a resized canvas alone would crop or overfill print/SNS variants. */
const formatStyles = `
body[data-format='preview_jpeg'] .page{--density:1}
body[data-format='preview_jpeg'] .newspaper{--np-scale:1}
body[data-format='sns_4x5_jpeg'] .page{--density:.9}
/* 4:5 は天地が浅いので、紙面家具は保ったまま本文を締め、長いトピックだけを省く。 */
body[data-format='sns_4x5_jpeg'] .newspaper{--np-scale:1;--np-spread-height:83%;--np-lower-height:13.3%;padding:calc(var(--safe) * .28) calc(var(--safe) * .36) calc(var(--safe) * .13)}
body[data-format='sns_4x5_jpeg'] .newspaper .np-top-rail{height:2%}
body[data-format='sns_4x5_jpeg'] .newspaper .newspaper-spread{margin-top:1.15%;grid-template-columns:83.5% minmax(0,1fr);gap:1.5%}
body[data-format='sns_4x5_jpeg'] .newspaper .np-editorial-band{gap:1.8%}
body[data-format='sns_4x5_jpeg'] .newspaper .np-message-card,body[data-format='sns_4x5_jpeg'] .newspaper .np-news-card{padding:4% 6%}
body[data-format='sns_4x5_jpeg'] .newspaper .np-feature-story{padding:1.2% 0 0 1.8%}
body[data-format='sns_4x5_jpeg'] .newspaper .np-lower-card-grid{margin-top:1.05%;gap:1%}
body[data-format='sns_4x5_jpeg'] .newspaper .np-topics-card li:nth-child(n+5){display:none}
/* 4:5 は参考組版の文字級数を維持し、紙面下端の補助欄だけを次頁相当として省く。 */
body[data-format='sns_4x5_jpeg'] .horizontal-newspaper{--hn-scale:1;--hn-row-teasers:10.4fr;--hn-row-lead:59.4fr;--hn-row-mid:13.9fr;grid-template-rows:2.4fr .8fr var(--hn-row-teasers) 1.3fr 10.1fr 1.1fr var(--hn-row-lead) .6fr var(--hn-row-mid) 0fr 0fr;padding:2.8% 4.25% 1%}
body[data-format='sns_4x5_jpeg'] .horizontal-newspaper.hn-no-teasers{--hn-row-teasers:0fr;--hn-row-lead:69.8fr}
body[data-format='sns_4x5_jpeg'] .horizontal-newspaper.hn-no-mid{--hn-row-mid:0fr;--hn-row-lead:73.3fr}
body[data-format='sns_4x5_jpeg'] .horizontal-newspaper.hn-no-teasers.hn-no-mid{--hn-row-lead:83.7fr}
body[data-format='sns_4x5_jpeg'] .horizontal-newspaper .hn-folio{font-size:calc(var(--page-w) * .0148 * var(--hn-scale));gap:1.8%}
body[data-format='sns_4x5_jpeg'] .horizontal-newspaper .hn-lead-spread{grid-template-columns:21.6% minmax(0,1fr);gap:1.25%}
body[data-format='sns_4x5_jpeg'] .horizontal-newspaper .hn-lead-spread.no-side{grid-template-columns:minmax(0,1fr);gap:0}
body[data-format='sns_4x5_jpeg'] .horizontal-newspaper .hn-side-rail{padding-right:10.5%}
body[data-format='sns_4x5_jpeg'] .horizontal-newspaper .hn-mid-grid>article,body[data-format='sns_4x5_jpeg'] .horizontal-newspaper .hn-mid-grid>figure{padding:3.6% 5.2%}
body[data-format='sns_4x5_jpeg'] .horizontal-newspaper .hn-lower-grid{display:none}
/* 4:5 が基準版。添付資料と同じ比率なので座標はテンプレート本体の値をそのまま使う。 */
body[data-format='sns_4x5_jpeg'] .magazine{--mag-scale:1}
body[data-format='sns_4x5_jpeg'] .storybook .story-photo{width:82%;height:45%}
body[data-format='sns_4x5_jpeg'] .growth-grid{grid-template-columns:36% 1fr;gap:5%}
body[data-format='sns_4x5_jpeg'] .timeline article:nth-child(n+6){display:none}

/* 9:16 は縦の余白を写真へ渡し、カード自体の物理寸法は幅基準のまま保つ。 */
body[data-format='story_9x16_jpeg'] .magazine{--mag-scale:.92}
body[data-format='story_9x16_jpeg'] .portrait-magazine .pm-photo-wash{background:linear-gradient(180deg,var(--pm-paper) 0 11.4%,rgba(251,250,246,.94) 14.6%,rgba(251,250,246,0) 24%),linear-gradient(90deg,rgba(251,250,246,.97) 0,rgba(251,250,246,.9) 21%,rgba(251,250,246,.18) 37%,rgba(251,250,246,0) 52%),linear-gradient(270deg,rgba(251,250,246,.78) 0,rgba(251,250,246,.1) 17%,rgba(251,250,246,0) 31%)}
body[data-format='story_9x16_jpeg'] .magazine .mag-headline{top:14.7%}
body[data-format='story_9x16_jpeg'] .magazine .mag-hero{top:25.7%;height:74.4%}
body[data-format='story_9x16_jpeg'] .magazine .mag-extra{top:11.7%}
body[data-format='story_9x16_jpeg'] .magazine .mag-achievement{top:39.5%}
body[data-format='story_9x16_jpeg'] .magazine .mag-favorite{top:57%}
body[data-format='story_9x16_jpeg'] .magazine .mag-message{top:74%}
body[data-format='story_9x16_jpeg'] .magazine .mag-record{top:31%}
body[data-format='story_9x16_jpeg'] .magazine .mag-topics{top:51%}
body[data-format='story_9x16_jpeg'] .magazine .mag-note{top:82.5%}
body[data-format='story_9x16_jpeg'] .magazine .mag-best-shot{right:1.4%;top:73.5%;width:28.4%;height:calc(var(--page-w) * .291);transform:rotate(3.8deg)}
body[data-format='story_9x16_jpeg'] .storybook h1{max-width:92%;margin-bottom:4%}
body[data-format='story_9x16_jpeg'] .storybook .story-photo{width:92%;height:52%;margin-inline:auto}
body[data-format='story_9x16_jpeg'] .storybook .story-copy{width:88%;max-height:21%;margin-top:6%}

body[data-format='a4_pdf'] .page{--density:.82}
/* A4/A3 share the preview canvas 1:1.414; page-relative type preserves the composition. */
body[data-format='a4_pdf'] .newspaper{--np-scale:1}
/* A4 はプレビューと同じ 1:1.414。字は判型そのものから引くので、上書きは要らない。 */
body[data-format='a4_pdf'] .growth .timeline article{max-height:28mm}
body[data-format='a3_pdf'] .page{--density:1.12}
body[data-format='a3_pdf'] .newspaper{--np-scale:1}
body[data-format='a3_pdf'] .growth-grid{grid-template-columns:42% 1fr}
body[data-format='a3_pdf'] .growth .timeline article{max-height:38mm;margin-bottom:5mm}

/* 2L は横長の扉版。左に題字と二つの編集枠、右に主写真を大きく置く。 */
body[data-format='two_l_jpeg'] .magazine{--mag-scale:.62}
body[data-format='two_l_jpeg'] .magazine .mag-tagline{left:2.2%;top:2.7%}
body[data-format='two_l_jpeg'] .magazine .mag-logo{left:2%;top:7%;width:42%;font-size:calc(var(--page-w) * .105 * var(--mag-scale))}
body[data-format='two_l_jpeg'] .magazine .mag-issue{left:29%;right:auto;top:3%;width:13%}
body[data-format='two_l_jpeg'] .magazine .mag-headline{left:2.4%;top:29%;width:42%;height:34%}
body[data-format='two_l_jpeg'] .magazine .mag-ribbon{font-size:calc(var(--page-w) * .024 * var(--mag-scale))}
body[data-format='two_l_jpeg'] .magazine .mag-headline h1{font-size:calc(var(--page-w) * .07 * var(--mag-scale) * var(--mag-name-fit,1))}
body[data-format='two_l_jpeg'] .magazine .mag-headline h2{font-size:calc(var(--page-w) * .047 * var(--mag-scale) * var(--mag-growth-fit,1))}
body[data-format='two_l_jpeg'] .magazine .mag-deck{max-width:78%;font-size:calc(var(--page-w) * .014 * var(--mag-scale))}
body[data-format='two_l_jpeg'] .magazine .mag-hero{left:46%;top:4.5%;width:51.8%;height:91%;border-radius:34% 6% 4% 3%/27% 6% 4% 5%;transform:rotate(-.8deg)}
body[data-format='two_l_jpeg'] .magazine .mag-extra{right:40.5%;top:2.5%;width:15.5%}
body[data-format='two_l_jpeg'] .magazine .mag-achievement{left:1.1%;top:66%;width:20%;height:30%}
body[data-format='two_l_jpeg'] .magazine .mag-favorite{left:22.3%;top:66%;width:20%;height:30%}
body[data-format='two_l_jpeg'] .magazine .mag-message,body[data-format='two_l_jpeg'] .magazine .mag-record,body[data-format='two_l_jpeg'] .magazine .mag-topics{display:none}
body[data-format='two_l_jpeg'] .magazine .mag-note{left:47%;top:69%;width:14%}
body[data-format='two_l_jpeg'] .magazine .mag-note{padding:1.4%}
body[data-format='two_l_jpeg'] .magazine .mag-best-shot{right:1.2%;top:66%;width:18%;height:29%;transform:rotate(3deg)}
body[data-format='two_l_jpeg'] .magazine>.brand{right:20.5%}
body[data-format='two_l_jpeg'] .storybook{display:grid;grid-template-columns:48% 1fr;grid-template-rows:auto auto 1fr;column-gap:6%;padding:var(--safe)}
body[data-format='two_l_jpeg'] .storybook>.eyebrow,body[data-format='two_l_jpeg'] .storybook>h1,body[data-format='two_l_jpeg'] .storybook .story-copy{grid-column:2}
body[data-format='two_l_jpeg'] .storybook>h1{max-width:100%;margin:2% 0 5%}
body[data-format='two_l_jpeg'] .storybook .story-photo{grid-column:1;grid-row:1/4;width:100%;height:100%;margin:0;border-radius:42% 42% 12px 12px/14% 14% 12px 12px}
body[data-format='two_l_jpeg'] .storybook .story-copy{width:100%;max-height:100%;margin:0}

/* はがきは幅基準の本文を少しだけ締め、カードの密度を保つ。 */
body[data-format='postcard_jpeg'] .magazine{--mag-scale:.94}
body[data-format='postcard_jpeg'] .storybook h1{max-width:92%}
body[data-format='postcard_jpeg'] .storybook .story-photo{width:88%;height:50%;margin-inline:auto}
body[data-format='postcard_jpeg'] .storybook .story-copy{width:88%;max-height:24%;margin-top:7%}
`;

/*
 * 本文ページ（2ページ目以降）
 *
 * 表紙面はどの判型でも固定枠なので、長い月の言葉は `clampCopy` で必ず削られる。削った
 * 分がどこにも出ないままだと「できあがった読み物だけを読めば足りる」が成立しないので、
 * PDF では表紙面のうしろに単段組みの本文ページを足し、`content` の全文をそのまま流す。
 * 表紙面が何を省いたかは `fitMultiplier` の自動調整も絡んで確定できないため、差分では
 * なく全文を載せる。JPEG は共有用の一枚という性質なので付けない。
 */

const BODY_FONT_RATIO = 0.0175;
const BODY_LINE_HEIGHT = 1.85;
/** 禁則処理と行末の端数で実際に入る量は理論値を下回るので、幅・高さともに余裕を残す。 */
const BODY_WIDTH_FILL = 0.94;
const BODY_HEIGHT_FILL = 0.9;
/** これ以下の行数しか置けないなら、切って渡すより次ページへ送ったほうが読みやすい。 */
const MIN_BODY_LINES = 2;

export type BodyBlockKind = 'title' | 'lead' | 'heading' | 'text' | 'caption' | 'note';

export interface BodyBlock {
  kind: BodyBlockKind;
  text: string;
}

export interface BodyPage {
  blocks: BodyBlock[];
}

export interface BodyPageMetrics {
  /** 本文1行に入る全角字数 */
  perLine: number;
  /** 1ページに入る本文の行数 */
  lines: number;
}

/**
 * 字送りは他テンプレートと同じ判型相対（`calc(var(--page-w) * …)`）なので、見出しや
 * キャプションの寸法も本文1行を単位にした比で表せる。`leadIn` はブロック間の余白。
 */
const bodyBlockScales: Record<
  BodyBlockKind,
  { perLine: number; lineCost: number; leadIn: number }
> = {
  title: { perLine: 0.62, lineCost: 1.22, leadIn: 0.5 },
  lead: { perLine: 1, lineCost: 1, leadIn: 0.5 },
  heading: { perLine: 0.74, lineCost: 1.1, leadIn: 1.2 },
  text: { perLine: 1, lineCost: 1, leadIn: 0.5 },
  caption: { perLine: 1.29, lineCost: 0.78, leadIn: 0.5 },
  note: { perLine: 0.94, lineCost: 1, leadIn: 0.5 },
};

/** 容量は判型の縦横比だけで決まる。1ページ目の組版とは独立に測れる。 */
export function bodyPageMetrics(format: ArtifactFormat): BodyPageMetrics {
  const profile = renderProfiles[format];
  const bodyFont = profile.width * BODY_FONT_RATIO;
  const perLine = Math.floor(
    ((profile.width - profile.safeMargin * 2) / bodyFont) * BODY_WIDTH_FILL,
  );
  // 柱（号数とページ番号）とその下の罫・余白が本文から取る高さ。
  const folio = profile.width * (0.0125 * 1.5 + 0.008 + 0.012);
  const available = profile.height - profile.safeMargin * 2 - folio;
  const lines = Math.floor((available / (bodyFont * BODY_LINE_HEIGHT)) * BODY_HEIGHT_FILL);
  return { perLine: Math.max(8, perLine), lines: Math.max(6, lines) };
}

function blockPerLine(kind: BodyBlockKind, perLine: number): number {
  return Math.max(4, Math.floor(perLine * bodyBlockScales[kind].perLine));
}

function countBodyLines(text: string, perLine: number): number {
  let lines = 0;
  for (const paragraph of text.split('\n')) {
    lines += Math.max(1, Math.ceil(paragraph.length / perLine));
  }
  return lines;
}

/**
 * `maxLines` 行に収まる位置で本文を二つに割る。捨てる文字は無く、返り値を連結すると
 * 入力と1文字も違わない（`clampCopy` の境界探索と同じ考え方だが、残りを次ページへ渡す）。
 */
function splitBodyText(text: string, perLine: number, maxLines: number): [string, string] {
  if (maxLines <= 0) return ['', text];
  let cursor = 0;
  let lines = 0;
  while (cursor < text.length && lines < maxLines) {
    const newline = text.indexOf('\n', cursor);
    cursor =
      newline !== -1 && newline - cursor <= perLine
        ? newline + 1
        : Math.min(text.length, cursor + perLine);
    lines += 1;
  }
  if (cursor >= text.length) return [text, ''];
  const head = text.slice(0, cursor);
  const stop = Math.max(head.lastIndexOf('。'), head.lastIndexOf('」'), head.lastIndexOf('\n'));
  const cut = stop >= Math.floor(cursor * 0.5) ? stop + 1 : cursor;
  return [text.slice(0, cut), text.slice(cut)];
}

/** 読み物の全文を、載せる順に並べた段落ブロック列。連結すると入力の全文になる。 */
export function issueBodyBlocks(content: GeneratedIssueContent): BodyBlock[] {
  const blocks: BodyBlock[] = [{ kind: 'title', text: content.title }];
  if (content.lead) blocks.push({ kind: 'lead', text: content.lead });
  for (const article of content.articles) {
    blocks.push({ kind: 'heading', text: article.headline });
    blocks.push({ kind: 'text', text: article.body });
    if (article.caption) blocks.push({ kind: 'caption', text: article.caption });
  }
  if (content.editorsNote) blocks.push({ kind: 'note', text: content.editorsNote });
  return blocks.filter((block) => block.text.length > 0);
}

export function paginateIssueCopy(
  content: GeneratedIssueContent,
  metrics: BodyPageMetrics,
): BodyPage[] {
  const pages: BodyPage[] = [];
  let current: BodyBlock[] = [];
  let used = 0;
  const flush = () => {
    if (current.length > 0) pages.push({ blocks: current });
    current = [];
    used = 0;
  };

  for (const block of issueBodyBlocks(content)) {
    const scale = bodyBlockScales[block.kind];
    const perLine = blockPerLine(block.kind, metrics.perLine);
    // 見出しだけを置いて本文を次ページへ送る泣き別れを避けるため、続きの数行を予約する。
    const keepWithNext = block.kind === 'heading' || block.kind === 'title' ? MIN_BODY_LINES : 0;
    let text = block.text;

    while (text.length > 0) {
      const gap = current.length > 0 ? scale.leadIn : 0;
      const room = metrics.lines - used - gap;
      const needed = countBodyLines(text, perLine) * scale.lineCost;
      if (needed + keepWithNext <= room) {
        current.push({ kind: block.kind, text });
        used += gap + needed;
        break;
      }
      const fittable = Math.floor(room / scale.lineCost);
      const [head, tail] =
        keepWithNext > 0 || fittable < MIN_BODY_LINES
          ? ['', text]
          : splitBodyText(text, perLine, fittable);
      if (head.length === 0) {
        if (current.length === 0) {
          // 空のページにも収まらない = 見積りが破綻している。黙って捨てるより、
          // そのまま載せて溢れをゲート（`data-overflow-check`）に検出させる。
          current.push({ kind: block.kind, text });
          break;
        }
        flush();
        continue;
      }
      current.push({ kind: block.kind, text: head });
      text = tail;
      flush();
    }
  }
  flush();
  return pages;
}

function bodyBlockMarkup(block: BodyBlock): string {
  const text = escapeHtml(block.text);
  switch (block.kind) {
    case 'title':
      return `<h2 class="body-title" data-body-block="title">${text}</h2>`;
    case 'heading':
      return `<h3 class="body-heading" data-body-block="heading">${text}</h3>`;
    case 'lead':
      return `<p class="body-text body-lead" data-body-block="lead">${text}</p>`;
    case 'caption':
      return `<p class="body-caption" data-body-block="caption">${text}</p>`;
    case 'note':
      return `<p class="body-text body-note" data-body-block="note">${text}</p>`;
    default:
      return `<p class="body-text" data-body-block="text">${text}</p>`;
  }
}

/**
 * 本文ページは文字だけで組む。写真は表紙面が一枚ずつ使い切る約束になっており、ここに
 * 出すと同じ写真が二度現れる。柱は号数とページ番号だけを持ち、次ページ案内は置かない。
 */
function bodyPagesMarkup(input: RenderIssueInput): string {
  if (!input.format.endsWith('_pdf')) return '';
  const pages = paginateIssueCopy(input.content, bodyPageMetrics(input.format));
  const label = displayField(input.content, 'issue-label', clampCopy(input.issueLabel, 28), {
    secondary: true,
  });
  return pages
    .map(
      (page, index) => `<main class="page issue-body-page" data-issue-body-page="${index + 1}">
    <div class="body-folio"><span${label.attributes}>${label.html}</span><span>${index + 1} / ${pages.length}</span></div>
    <div class="body-copy" data-overflow-check>${page.blocks.map(bodyBlockMarkup).join('')}</div>
  </main>`,
    )
    .join('');
}

/** 表紙面1枚＋本文ページ。単一ページ前提で高さを計算している呼び出し側が使う。 */
export function issuePageCount(input: RenderIssueInput): number {
  if (!input.format.endsWith('_pdf')) return 1;
  return 1 + paginateIssueCopy(input.content, bodyPageMetrics(input.format)).length;
}

export function renderIssueDocument(input: RenderIssueInput): string {
  const availableVersion = templateManifests[input.template].version;
  if (input.templateVersion !== availableVersion) {
    throw new Error(
      `Template ${input.template} version ${input.templateVersion} is unavailable (current: ${availableVersion})`,
    );
  }
  assertSupportedFormat(input.template, input.format);
  const normalizedInput = normalizeIssueDisplayMetadata(input);
  const profile = renderProfiles[normalizedInput.format];
  const size = `${profile.width}${profile.unit}`;
  const height = `${profile.height}${profile.unit}`;
  const safe = `${profile.safeMargin}${profile.unit}`;
  const showPremiumWatermark = input.showPremiumWatermark === true;
  const copyDensity =
    normalizedInput.content.title.length > 40 ||
    (normalizedInput.content.lead?.length ?? 0) > 180 ||
    (normalizedInput.content.editorsNote?.length ?? 0) > 180 ||
    normalizedInput.content.articles.some((article) => article.body.length > 600)
      ? 'dense'
      : 'regular';
  const body = {
    'classic-newspaper': classicNewspaper,
    'horizontal-newspaper': horizontalNewspaper,
    'magazine-cover': magazineCover,
    'magazine-cover-portrait': portraitMagazineCover,
    'magazine-cover-mode': editorialSeriesCover,
    'magazine-cover-weekend': editorialSeriesCover,
    'magazine-cover-romantic': editorialSeriesCover,
    'magazine-cover-journal': editorialSeriesCover,
    'storybook-page': storybookPage,
    'growth-report': growthReport,
  }[normalizedInput.template](normalizedInput);

  return `<!doctype html>
<html lang="ja"><head><meta charset="utf-8" /><meta name="viewport" content="width=device-width,initial-scale=1" />
<title>${escapeHtml(normalizedInput.content.title)}</title>
<style>${baseStyles}:root{--page-w:${size};--page-h:${height};--safe:${safe}}@page{size:${size} ${height};margin:0}${templateStyles[normalizedInput.template]}${formatStyles}${magazineEditorialStyles}${quietCoverStyles}${newspaperEditorialStyles}${editorialSeriesStyles}${showPremiumWatermark ? premiumWatermarkStyles : ''}</style>
</head><body data-template="${normalizedInput.template}" data-template-version="${normalizedInput.templateVersion}" data-format="${normalizedInput.format}" data-copy-density="${copyDensity}"${showPremiumWatermark ? ' data-premium-watermark="true"' : ''}>${body}${bodyPagesMarkup(normalizedInput)}</body></html>`;
}

export function extractOverflowSelectors(): string[] {
  return ['[data-overflow-check]'];
}
